<section
  class="workflow-drafts"
  [class.workflow-drafts--workbench]="currentDraft() !== null"
  [attr.aria-labelledby]="
    currentDraft() ? 'workflow-draft-editor-title' : 'workflow-drafts-title'
  "
  data-testid="workflow-draft-workspace"
>
  <div class="workflow-drafts__browse-intro" [hidden]="currentDraft() !== null">
    <header class="workflow-drafts__header">
      <div>
        <h2 #draftListHeading id="workflow-drafts-title" tabindex="-1">
          {{ draftQuery().workflowCode ? 'Drafts for ' + draftQuery().workflowCode : 'Drafts' }}
        </h2>
        <p>
          {{
            canAuthor()
              ? draftQuery().workflowCode
                ? "Start from the latest published version. If an editable draft already exists for that version, it will reopen."
                : "Continue work in progress or create a new business process."
              : "Review and open saved workflow drafts."
          }}
        </p>
      </div>
      <button *ngIf="canCreateWorkflowDraft() && draftQuery().workflowCode" uiButton type="button"
        data-testid="create-workflow-revision" [disabled]="isDraftEditorInteractionBlocked()"
        (click)="beginWorkflowDraft()">{{ isRunningAuthoringAction() ? 'Opening draft…' : 'New draft' }}</button>
      <ui-button-group
        *ngIf="canAuthor() && showAuthoringEntryButtons"
        role="group"
        aria-label="Start workflow authoring"
        [buttonsConfig]="authoringEntryButtons()"
        (buttonClick)="onAuthoringEntryButtonClick($event)"
      ></ui-button-group>
    </header>

    <section
      *ngIf="authoringMode() as mode"
      class="workflow-drafts__authoring"
      aria-labelledby="workflow-draft-authoring-title"
      data-testid="draft-authoring-form"
    >
      <div>
        <h3 id="workflow-draft-authoring-title">
          {{
            mode === "clone" ? "Clone selected version" : "Create a workflow"
          }}
        </h3>
        <p *ngIf="mode === 'create'">
          Name the business process you want to build. We’ll create an editable
          draft, then you can add steps and choose what data each action
          receives and saves. Publish when it is ready to use.
        </p>
        <p *ngIf="mode === 'clone' && selectedDefinition">
          Base: {{ selectedDefinition.workflowCode }} /
          {{ selectedDefinition.definitionVersionCode }}. Published step codes
          remain locked compatibility keys.
        </p>
      </div>

      <p
        *ngIf="mode === 'create'"
        class="workflow-setup__summary"
        data-testid="new-workflow-summary"
      >
        {{ setupSummary(authoringFormValues) }}
      </p>
      <ui-form
        #authoringForm
        instanceId="workflow-authoring-basics"
        aria-label="Workflow draft authoring details"
        [config]="authoringFormConfig"
        [formValues]="authoringFormValues"
        [disabled]="isDraftMutationInProgress()"
        (formValuesChange)="onAuthoringFormChange($event, 'basics')"
      ></ui-form>

      <ng-container *ngIf="mode === 'create'">
        <p class="workflow-setup__help">
          Every run has its own identity. Records such as cases, people, and
          hearings can be passed to actions when needed.
        </p>
        <details
          #authoringAvailabilityDetails
          class="workflow-setup__disclosure"
          data-testid="new-workflow-availability"
        >
          <summary>
            Availability
            <span>{{ availabilitySummary(authoringFormValues) }}</span>
          </summary>
          <div class="workflow-setup__disclosure-body">
            <p class="workflow-setup__help">
              Keep all locations, or limit where this process may be activated.
              Access permissions are managed separately.
            </p>
            <ui-form
              #authoringAvailabilityForm
              instanceId="workflow-authoring-availability"
              aria-label="New process availability"
              [config]="authoringAvailabilityFormConfig"
              [formValues]="authoringFormValues"
              [disabled]="isDraftMutationInProgress()"
              (formValuesChange)="onAuthoringFormChange($event, 'availability')"
            ></ui-form>
            <p class="workflow-setup__help">
              {{ scopeKeyGuidance(authoringFormValues.scopeTypeCode) }}
            </p>
          </div>
        </details>
      </ng-container>

      <ui-button-group
        [buttonsConfig]="authoringActionButtons()"
        (buttonClick)="onAuthoringActionButtonClick($event)"
      ></ui-button-group>
    </section>

    <p
      *ngIf="authoringMessage() as message"
      class="workflow-drafts__notice"
      role="status"
      aria-live="polite"
    >
      {{ message }}
    </p>

    <p
      *ngIf="isRunningAuthoringAction()"
      class="workflow-drafts__state"
      role="status"
      aria-live="polite"
    >
      {{
        authoringMode() === "clone"
          ? "Cloning the selected version."
          : "Creating the draft."
      }}
    </p>
  </div>

  <div
    *ngIf="!draftReadError() && operationError() as error"
    #operationErrorSummary
    class="workflow-drafts__error"
    data-testid="draft-operation-error"
    role="alert"
    aria-live="assertive"
    tabindex="-1"
  >
    <strong>{{ error.title }}</strong>
    <ul>
      <li *ngFor="let message of error.messages">{{ message }}</li>
    </ul>
    <p
      class="workflow-drafts__error-meta"
      *ngIf="error.reasonCode || error.correlationId"
    >
      <span *ngIf="error.reasonCode">Reason: {{ error.reasonCode }}</span>
      <span *ngIf="error.correlationId"
        >Correlation ID: {{ error.correlationId }}</span
      >
    </p>
    <button
      uiButton
      [text]="true"
      [outlined]="false"
      *ngIf="failedDraftLoad()"
      type="button"
      class="workflow-drafts__text-button"
      data-testid="retry-draft-load"
      [disabled]="isLoadingDraft() || isDraftMutationInProgress()"
      (click)="retryFailedDraftLoad()"
    >
      Retry opening draft
    </button>
    <button
      uiButton
      [text]="true"
      [outlined]="false"
      *ngIf="error.statusCode === 412 && currentDraft()"
      type="button"
      class="workflow-drafts__text-button"
      data-testid="reload-latest-draft"
      [disabled]="isLoadingDraft() || isDraftMutationInProgress()"
      (click)="reloadLatestAfterConflict()"
    >
      Reload latest revision
    </button>
  </div>

  <section
    class="workflow-drafts__catalog"
    aria-labelledby="workflow-draft-list-title"
    [hidden]="currentDraft() !== null"
  >
    <div class="workflow-drafts__section-heading">
      <div>
        <h3 id="workflow-draft-list-title">Saved drafts</h3>
        <p>Open a draft to continue from its latest saved revision.</p>
      </div>
      <span *ngIf="draftPage()" data-testid="draft-result-count">{{
        draftResultCountLabel()
      }}</span>
    </div>

    <div
      class="workflow-drafts__filter-bar"
      role="group"
      aria-label="Saved draft filters"
    >
      <ui-form
        class="workflow-drafts__filters"
        [config]="primaryDraftFilterFormConfig"
        [(formValues)]="primaryDraftFilterFormValues"
        (submit)="onDraftFilterSubmit($event)"
      ></ui-form>

      <div class="workflow-drafts__filter-tools">
        <button
          uiButton
          type="button"
          class="workflow-drafts__advanced-toggle"
          [attr.aria-expanded]="advancedDraftFiltersOpen"
          aria-controls="workflow-drafts-advanced-filters"
          (click)="toggleAdvancedDraftFilters()"
        >
          {{ advancedDraftFiltersOpen ? "Fewer filters" : "More filters" }}
        </button>
        <ui-button-group
          [buttonsConfig]="draftFilterButtons()"
          (buttonClick)="onDraftFilterButtonClick($event)"
        ></ui-button-group>
      </div>
    </div>

    <div *ngIf="draftQuery().workflowCode || draftQuery().baseDefinitionVersionId" role="group" aria-label="Draft discovery scope" data-draft-scope>
      <button *ngIf="draftQuery().workflowCode" uiButton type="button" data-clear-draft-workflow [disabled]="isLoadingDrafts()" (click)="clearDraftDiscoveryScope()">Workflow: {{ draftQuery().workflowCode }} · Clear</button>
      <button *ngIf="draftQuery().baseDefinitionVersionId" uiButton type="button" data-clear-draft-base [disabled]="isLoadingDrafts()" (click)="clearDraftBaseVersion()">Based on {{ draftBaseVersionLabel() }} · Clear</button>
    </div>

    <label class="workflow-drafts__archive-filter"><input uiControl type="checkbox" data-include-hidden-drafts [ngModel]="draftQuery().includeHidden === 'true'" (ngModelChange)="setIncludeHiddenDrafts($event)" [disabled]="isLoadingDrafts() || draftPage()?.sharedVisibilityAvailable !== true"> Include hidden</label>
    <p *ngIf="draftPage() && draftPage()?.sharedVisibilityAvailable !== true" role="status">Shared visibility is unavailable on this installation.</p>
    <section
      id="workflow-drafts-advanced-filters"
      class="workflow-drafts__advanced-filters"
      aria-label="Additional saved draft filters"
      [hidden]="!advancedDraftFiltersOpen"
    >
      <ui-form
        [config]="advancedDraftFilterFormConfig"
        [(formValues)]="advancedDraftFilterFormValues"
        (submit)="onDraftFilterSubmit($event)"
      ></ui-form>
      <label class="workflow-drafts__archive-filter">
        <input
          uiControl
          type="checkbox"
          data-testid="include-archived-drafts"
          [ngModel]="draftQuery().includeArchived === 'true'"
          (ngModelChange)="setIncludeArchivedDrafts($event)"
          [disabled]="isLoadingDrafts()"
        />
        Include archived drafts (read only)
      </label>
    </section>

    <ui-standard-state
      *ngIf="isLoadingDrafts()"
      kind="loading"
      [message]="draftListStatusMessage()"
      data-testid="draft-list-loading"
    ></ui-standard-state>

    <ui-standard-state
      *ngIf="draftListError()"
      [kind]="draftListReadState()"
      [message]="draftListErrorMessage()"
      [recoveryLabel]="
        draftListReadState() === 'forbidden' ? undefined : 'Try again'
      "
      [recoveryDisabled]="isLoadingDrafts()"
      (recovery)="retryDraftList()"
      data-testid="draft-list-error"
    ></ui-standard-state>

    <ui-standard-state
      *ngIf="
        !isLoadingDrafts() && draftPage()?.totalCount === 0 && !draftListError()
      "
      kind="empty"
      [message]="
        hasActiveDraftFilters()
          ? 'No drafts match these filters. Clear a filter and try again.'
          : 'No saved drafts are available yet.'
      "
      [recoveryLabel]="hasActiveDraftFilters() ? 'Clear filters' : undefined"
      (recovery)="clearDraftFilters()"
      data-testid="draft-list-empty"
    ></ui-standard-state>

    <ng-container *ngIf="draftPage() as page">
      <div
        *ngIf="page.items.length > 0"
        class="workflow-drafts__grid"
        [attr.aria-busy]="isLoadingDrafts()"
      >
        <ui-grid
          width="fill"
          containerClass="no-panel workflow-registry-draft-grid"
          [rowData]="page.items"
          [columnDefs]="draftColumnDefs"
          [defaultColDef]="defaultDraftColumnDef"
          [agGridOptions]="draftGridOptions"
          [rowHeight]="64"
          [showPanelHeader]="false"
          [showBlankRowsOnLastPage]="false"
          [autoHidePaginator]="true"
        ></ui-grid>
      </div>
    </ng-container>

    <footer *ngIf="draftPage()?.totalCount" class="workflow-drafts__pagination">
      <div>
        <span data-testid="draft-page-range" aria-live="polite">
          Page {{ draftPage()?.pageNumber }} &middot; {{ pageRangeLabel() }}
        </span>
        <span class="workflow-drafts__updated" *ngIf="draftListAsOfLabel()">
          Results as of {{ draftListAsOfLabel() }}
        </span>
        <span class="visually-hidden">
          Drafts are ordered by most recently updated. Column sorting affects
          only this loaded page.
        </span>
      </div>
      <ui-button-group
        role="group"
        aria-label="Saved draft pages"
        [buttonsConfig]="paginationButtons()"
        (buttonClick)="onPaginationButtonClick($event)"
      ></ui-button-group>
    </footer>
  </section>

  <ui-standard-state
    *ngIf="isLoadingDraft()"
    kind="loading"
    message="Loading draft editor."
    data-testid="draft-loading"
  ></ui-standard-state>

  <div
    *ngIf="draftReadError()"
    #draftReadErrorSummary
    data-testid="draft-read-error"
    tabindex="-1"
  >
    <ui-standard-state
      [kind]="draftReadState()"
      [message]="draftReadMessage()"
      [recoveryLabel]="failedDraftLoad() ? 'Retry opening draft' : undefined"
      [recoveryDisabled]="isLoadingDraft() || isDraftMutationInProgress()"
      [attr.data-testid]="failedDraftLoad() ? 'retry-draft-load' : null"
      (recovery)="retryFailedDraftLoad()"
    ></ui-standard-state>
  </div>

  <section
    *ngIf="currentDraft() as draft"
    class="workflow-drafts__editor"
    [class.workflow-drafts__editor--step]="
      isFocusedEditor()
    "
    [class.workflow-drafts__editor--overview]="
      !mapSelection() && !isAddingStep() && mapInspectorPane() === 'overview'
    "
    aria-labelledby="workflow-draft-editor-title"
    data-testid="draft-editor"
  >
    <div class="workflow-workbench__sticky">
      <header class="workflow-workbench__command-bar">
        <button
          uiButton
          [text]="true"
          [outlined]="false"
          type="button"
          class="workflow-workbench__back"
          aria-label="Back to drafts"
          title="Back to drafts"
          [disabled]="isDraftMutationInProgress()"
          (click)="closeDraft()"
        >
          <i class="pi pi-arrow-left" aria-hidden="true"></i>
          <span class="visually-hidden">Back to drafts</span>
        </button>

        <div class="workflow-workbench__identity">
          <h2
            #draftEditorHeading
            id="workflow-draft-editor-title"
            tabindex="-1"
            [attr.title]="(workingDocument()?.displayName || draft.workflowCode) + ' · ' + draft.workflowCode"
          >
            {{ workingDocument()?.displayName || draft.workflowCode }}
          </h2>
          <p [attr.title]="'Version ' + draft.document.definitionVersionCode + ' · Saved revision ' + draft.revision">
            {{
              isArchivedDraft()
                ? "Archived · read only"
                : "Draft"
            }}
          </p>
        </div>

        <div
          class="workflow-workbench__change-state"
          data-testid="draft-change-state"
          [class.workflow-workbench__change-state--pending]="
            hasUnsavedChanges()
          "
          [attr.title]="draftChangeDetail()"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <i
            [class]="
              isSaving() || isLoadingDraft()
                ? 'pi pi-spinner pi-spin'
                : hasUnsavedChanges()
                  ? 'pi pi-circle-fill'
                  : 'pi pi-check-circle'
            "
            aria-hidden="true"
          ></i>
          <span>{{ draftChangeLabel() }}</span>
        </div>

        <ui-button-group
          class="workflow-workbench__actions"
          aria-label="Draft editor actions"
          [buttonsConfig]="primaryEditorActionButtons()"
          (buttonClick)="onEditorActionButtonClick($event)"
        ></ui-button-group>
        <details
          *ngIf="!isArchivedDraft()"
          #draftActions
          class="workflow-context-actions"
          (keydown.escape)="closeContextActions(draftActions)"
        >
          <summary>Draft actions</summary>
          <div
            class="workflow-context-actions__items"
            aria-label="More draft actions"
          >
            <ui-button
              *ngFor="let button of secondaryEditorActionButtons()"
              [config]="button"
              (click)="
                onEditorActionButtonClick(button); draftActions.open = false
              "
            />
          </div>
        </details>
      </header>
    </div>
    <aside
      *ngIf="isArchivedDraft()"
      class="workflow-drafts__recovery"
      aria-label="Archived draft"
      data-testid="archived-draft-banner"
    >
      <strong>Archived draft · read only</strong>
      <p>
        This source draft was published{{
          draft.publishedDefinitionVersionCode
            ? " as version " + draft.publishedDefinitionVersionCode
            : ""
        }}{{
          draft.publishedRevision
            ? " from revision " + draft.publishedRevision
            : ""
        }}. Make further changes in a new draft; existing runs keep their
        published version.
      </p>
      <p
        *ngIf="
          draft.publishedRevision && draft.revision > draft.publishedRevision
        "
      >
        Later changes saved to this old draft have been retained here for
        reference. They are not part of the published version.
      </p>
      <p *ngIf="hasOpenDraftUnsavedChanges()">
        Your local changes and pending fields remain in this tab. Download a
        local copy before leaving. They cannot be saved to this archived draft.
      </p>
      <div
        class="workflow-archived-draft__actions"
        role="group"
        aria-label="Archived draft next steps"
      >
        <button
          uiButton
          type="button"
          *ngIf="draft.publishedDefinitionVersionCode"
          (click)="openPublishedSource()"
          [disabled]="isDraftMutationInProgress()"
        >
          View published version
        </button>
        <button
          uiButton
          type="button"
          *ngIf="canCreateWorkflowDraft()"
          [disabled]="
            archivedVersionLoading() ||
            !archivedPublishedVersion() ||
            isDraftMutationInProgress()
          "
          (click)="openArchivedDraftAction()"
        >
          {{ archivedDraftActionLabel() }}
        </button>
        <button
          uiButton
          type="button"
          *ngIf="archivedVersionError()"
          (click)="retryArchivedVersion()"
        >
          Retry draft availability
        </button>
        <button
          uiButton
          type="button"
          *ngIf="hasOpenDraftUnsavedChanges()"
          (click)="downloadRecoveryCopy()"
        >
          Download local copy
        </button>
        <button
          uiButton
          type="button"
          *ngIf="hasOpenDraftUnsavedChanges()"
          (click)="discardArchivedLocalChanges()"
        >
          Discard local changes
        </button>
      </div>
    </aside>

    <ui-tab-group
      class="workflow-workbench__tabs"
      [stretchTabs]="false"
      [navigationTabs]="workbenchTabs()"
      [activeTabId]="activeWorkbenchTab()"
      navigationAriaLabel="Draft work areas"
      (tabRequested)="selectWorkbenchTab($event)"
    >
      <p
        *ngIf="editorMessage() as message"
        class="workflow-drafts__notice"
        role="status"
        aria-live="polite"
      >
        {{ message }}
      </p>

      <p
        *ngIf="isValidating()"
        class="workflow-drafts__state"
        role="status"
        aria-live="polite"
      >
        Validating the saved draft.
      </p>

      <ng-container *ngIf="activeWorkbenchView() !== 'review'">
        <aside
          *ngIf="validationHandoff() as finding"
          class="workflow-validation-handoff"
          role="note"
          aria-label="Validation correction"
        >
          <div>
            <strong
              >{{ diagnosticAreaLabel(finding.diagnostic) }} · Finding from
              saved revision {{ finding.revision }}</strong
            >
            <p>{{ diagnosticCorrectionHint(finding.diagnostic) }}</p>
            <details
              *ngIf="
                diagnosticCorrectionHint(finding.diagnostic) !==
                finding.diagnostic.message
              "
            >
              <summary>Validation details</summary>
              {{ finding.diagnostic.message }}
            </details>
            <small>Save and validate again after correcting it.</small>
          </div>
          <div class="workflow-validation-handoff__actions">
            <span *ngIf="reviewCorrectionPosition() as position" role="status">
              Issue {{ position.index }} of {{ position.total }}
            </span>
            <button uiButton type="button"
              [disabled]="isDraftEditorInteractionBlocked() || !nextReviewFinding()"
              (click)="focusNextReviewFinding()">Next issue</button>
            <button uiButton type="button" [text]="true" [outlined]="false"
              [disabled]="isDraftEditorInteractionBlocked()"
              (click)="returnToValidationReview()">Back to findings</button>
          </div>
        </aside>
        <aside
          *ngIf="semanticReviewHandoff() as change"
          class="workflow-validation-handoff"
          role="note"
          aria-label="Saved change context"
        >
          <div>
            <strong>Change from saved revision {{ change.revision }}</strong>
            <p>{{ change.summary }}</p>
            <small
              >Local edits are included after you save and refresh the
              comparison.</small
            >
          </div>
          <button
            [wrap]="true"
            uiButton
            [text]="true"
            [outlined]="false"
            type="button"
            class="workflow-drafts__row-button"
            [disabled]="isDraftEditorInteractionBlocked()"
            (click)="returnToSavedChanges()"
          >
            Back to saved changes
          </button>
        </aside>
      </ng-container>

      <p
        *ngIf="recoveryStorageWarning() && !recoveryDocument()"
        class="workflow-drafts__state"
        role="alert"
      >
        {{ recoveryStorageWarning() }}
        <button
          uiButton
          [text]="true"
          [outlined]="false"
          *ngIf="documentDirty() && canAuthor()"
          type="button"
          class="workflow-drafts__text-button"
          (click)="downloadRecoveryCopy()"
        >
          Download recovery copy
        </button>
      </p>

      <aside
        *ngIf="archivedPendingInputs().length && !isArchivedDraft()"
        class="workflow-drafts__recovery"
        data-testid="draft-captured-pending-fields"
        aria-label="Captured pending fields"
      >
        <div>
          <strong>Captured pending fields</strong>
          <p>Field values captured during recovery remain in the download. They may include input whose original owner changed. Saving the document does not apply these captured values.</p>
        </div>
        <div class="workflow-drafts__recovery-actions">
          <button uiButton type="button" [text]="true" [outlined]="false"
            (click)="downloadRecoveryCopy()">Download recovery copy</button>
          <button uiButton type="button" [text]="true" [outlined]="false"
            [disabled]="isDraftMutationInProgress() || isLoadingDraft()"
            (click)="discardCapturedRecoveryInputs()">Discard captured fields</button>
        </div>
      </aside>

      <section
        *ngIf="recoveryDocument() && !isArchivedDraft()"
        class="workflow-drafts__recovery"
        data-testid="draft-conflict-recovery"
        aria-label="Recover unsaved edits"
      >
        <div>
          <strong>Recover unsaved edits</strong>
          <p *ngIf="!hasReloadedLatestAfterConflict()">
            Another save changed this draft. Your edits are preserved. Reload
            the latest revision to compare them.
          </p>
          <p *ngIf="hasReloadedLatestAfterConflict()">
            Independent edits are combined automatically. For settings changed
            in both copies, choose the edit to keep before reviewing the
            combined draft.
          </p>
          <p role="status">
            {{
              recoveryStorageWarning() ||
                "Your recovery copy stays in this browser tab through a refresh, until you save or discard it."
            }}
          </p>
        </div>
        <div
          *ngIf="recoveryComparison() as comparison"
          class="workflow-drafts__recovery-comparison"
        >
          <p *ngIf="!comparison.conflicts.length">
            No overlapping edits. Your changes can be combined with the latest
            saved revision.
          </p>
          <fieldset
            *ngFor="
              let conflict of comparison.conflicts;
              trackBy: trackRecoveryConflict
            "
            [attr.aria-label]="conflict.label"
          >
            <legend>{{ conflict.label }}</legend>
            <details>
              <summary>Previously saved value</summary>
              <pre>{{
                recoveryValue(conflict.base, conflict.basePresent)
              }}</pre>
            </details>
            <div class="workflow-drafts__recovery-options">
              <div>
                <strong>My edit</strong>
                <pre>{{
                  recoveryValue(conflict.local, conflict.localPresent)
                }}</pre>
                <button
                  uiButton
                  [text]="true"
                  [outlined]="false"
                  type="button"
                  class="workflow-drafts__text-button"
                  [attr.aria-pressed]="conflict.choice === 'local'"
                  [disabled]="areRecoveryActionsBlocked()"
                  (click)="chooseRecoveryEdit(conflict.path, 'local')"
                >
                  Use my edit
                </button>
              </div>
              <div>
                <strong>Latest saved edit</strong>
                <pre>{{
                  recoveryValue(conflict.latest, conflict.latestPresent)
                }}</pre>
                <button
                  uiButton
                  [text]="true"
                  [outlined]="false"
                  type="button"
                  class="workflow-drafts__text-button"
                  [attr.aria-pressed]="conflict.choice === 'latest'"
                  [disabled]="areRecoveryActionsBlocked()"
                  (click)="chooseRecoveryEdit(conflict.path, 'latest')"
                >
                  Use latest saved edit
                </button>
              </div>
            </div>
          </fieldset>
          <p *ngIf="comparison.unresolvedPaths.length" role="status">
            {{ comparison.unresolvedPaths.length }} setting(s) still need a
            choice.
          </p>
        </div>
        <div class="workflow-drafts__inline-actions">
          <button
            uiButton
            [text]="true"
            [outlined]="false"
            type="button"
            class="workflow-drafts__text-button"
            [disabled]="
              areRecoveryActionsBlocked() || !recoveryComparison()?.document
            "
            (click)="restoreRecoveryDocument()"
          >
            Review combined draft
          </button>
          <button
            uiButton
            [text]="true"
            [outlined]="false"
            type="button"
            class="workflow-drafts__text-button"
            [disabled]="areRecoveryActionsBlocked()"
            (click)="discardRecoveryDocument()"
          >
            Discard my recovery copy
          </button>
          <button
            uiButton
            [text]="true"
            [outlined]="false"
            type="button"
            class="workflow-drafts__text-button"
            (click)="downloadRecoveryCopy()"
          >
            Download recovery copy
          </button>
        </div>
      </section>

      <p
        *ngIf="
          (hasUnappliedEditorChanges() && !isAddingStep()) || recoveryDocument()
        "
        class="workflow-workbench__save-guidance"
        data-testid="draft-save-guidance"
        role="status"
        aria-live="polite"
      >
        <span>{{ draftChangeDetail() }}</span>
        <button
          uiButton
          [text]="true"
          [outlined]="false"
          *ngIf="pendingWorkbenchView()"
          type="button"
          class="workflow-drafts__text-button"
          (click)="returnToPendingChanges()"
        >
          {{
            pendingWorkbenchAreaCount() > 1
              ? "Review pending changes"
              : "Return to changes"
          }}
        </button>
      </p>

      <details
        #dryRunSection
        class="workflow-connected-dry-run"
        [hidden]="!dryRunExpanded()"
        [open]="dryRunExpanded()"
        (toggle)="setDryRunExpanded(dryRunSection.open)"
        tabindex="-1"
      >
        <summary>Connected process dry run</summary>
        <phms-workflow-draft-dry-run
          *ngIf="dryRunOpened()"
          [draft]="draft"
          [workingDocument]="workingDocument()"
          [unsavedSettings]="documentDirty()"
          [unappliedChanges]="hasUnappliedEditorChanges()"
          [sourceLoading]="isLoadingDraft()"
          [recoveryPending]="!!recoveryDocument()"
          [disabled]="isDraftMutationInProgress()"
        />
      </details>


      <div
        class="workflow-designer-layout"
        [hidden]="activeWorkbenchView() === 'review'"
      >
        <aside
          id="workflow-process-outline"
          class="workflow-process-rail"
          [hidden]="activeWorkbenchView() !== 'map' || isFocusedEditor()"
          [class.workflow-process-rail--drawer-open]="stepsDrawerOpen()"
          [class.workflow-process-rail--collapsed]="!processRailExpanded()"
          (keydown.escape)="$event.stopPropagation(); closeStepsDrawer()"
          aria-label="Workflow process"
        >
          <phms-workflow-designer-step-outline
            [entries]="designerOutlineEntries()" [selectedNodeId]="selectedMapNode()?.id ?? null"
            [blocked]="isDraftEditorInteractionBlocked()" [expanded]="processRailExpanded()" [drawerOpen]="stepsDrawerOpen()"
            (nodeRequested)="inspectStepFromOutline($event)" (issuesRequested)="openOutlineIssues($event)"
            (toggleRequested)="processRailExpanded.set(!processRailExpanded())" (closeRequested)="closeStepsDrawer()" />
        </aside>
        <div class="workflow-designer-content">
          <section
            id="workflow-workbench-panel-map" #inventoryMapPanel
            class="workflow-workbench__panel workflow-workbench__panel--map"
            role="region"
            aria-labelledby="workflow-workbench-tab-map"
            [hidden]="activeWorkbenchView() !== 'map'"
          >
            <h3 class="visually-hidden">Workflow map</h3>

            <phms-platform-workflow-draft-graph-preview
              [draft]="draft"
              [document]="workingDocument()"
              [recoveryCopyPending]="recoveryDocument() !== null"
              [recoveryLatestLoaded]="hasReloadedLatestAfterConflict()"
              [sourceLoading]="isLoadingDraft()"
              [contextualInspector]="true"
              [inspectorStandalone]="isFocusedEditor()"
              [selectionControlled]="true"
              [selectedRendererElementId]="selectedMapRendererId()"
              (editNodeRequested)="openNodeFromGraph($event)"
              (editEdgeRequested)="openEdgeFromGraph($event)"
              (decisionGateRequested)="openDecisionGateFromGraph($event)"
              (decisionScopeRequested)="openDecisionScopeFromGraph($event)"
              (selectionRequested)="onMapSelectionRequested($event)"
            >
              <ng-container workflowGraphToolbarActions>
                <button uiButton [text]="true" [outlined]="false" type="button" *ngIf="mapSelection()"
                  aria-label="Workflow overview" [disabled]="isDraftEditorInteractionBlocked()" (click)="showWholeWorkflowOverview()">Overview</button>
      <button #stepsDrawerToggle uiButton type="button" class="workflow-steps-drawer-toggle"
        [hidden]="activeWorkbenchView() !== 'map' || isFocusedEditor()"
        [attr.aria-expanded]="stepsDrawerOpen()" aria-controls="workflow-process-outline"
        (click)="toggleStepsDrawer()">Steps</button>
                <button uiButton type="button" *ngIf="canAuthor()" [cdkMenuTriggerFor]="addStepMenu"
                  (cdkMenuOpened)="captureAddStepMenuContext()" [disabled]="isArchivedDraft() || isDraftEditorInteractionBlocked()">
                  <i class="pi pi-plus" aria-hidden="true"></i> Add step
                </button>
                <ng-template #addStepMenu>
                  <div cdkMenu class="workflow-add-step-menu" aria-label="Add step">
                    <button uiButton cdkMenuItem type="button" [cdkMenuItemDisabled]="isDraftEditorInteractionBlocked() || !canAuthor() || isArchivedDraft()"
                      (cdkMenuItemTriggered)="addStepFromMenu('work')"><i class="pi pi-user" aria-hidden="true"></i> Work step</button>
                    <button uiButton cdkMenuItem type="button" [cdkMenuItemDisabled]="isDraftEditorInteractionBlocked() || !canAuthor() || isArchivedDraft()"
                      (cdkMenuItemTriggered)="addStepFromMenu('automated')"><i class="pi pi-bolt" aria-hidden="true"></i> Automated step</button>
                  </div>
                </ng-template>
              </ng-container>
              <div workflowGraphInspector class="workflow-map-inspector">
                <phms-workflow-step-inspection #stepInspection tabindex="-1"
                  *ngIf="isInspectingStep() && selectedMapNode() as node"
                  [node]="node" [overview]="selectedStepOverview()" [document]="workingDocument()"
                  [blocked]="isDraftEditorInteractionBlocked()"
                  (openEditor)="openNodeFromGraph(node.id)" />
                <phms-workflow-route-inspection #routeInspection tabindex="-1"
                  *ngIf="isInspectingRoute() && selectedRouteInspection() as route"
                  [route]="route" [blocked]="isDraftEditorInteractionBlocked()"
                  (openEditor)="openRoutingEditor($event)" />
                <div class="workflow-focused-editor" [class.workflow-focused-editor--step]="stepScopeTabs().length > 0" [hidden]="isInspectingStep() || isInspectingRoute()">


                <p *ngIf="editingRouteSelection() && focusedRouteIndex() === null" role="status">
                  The opened route is no longer uniquely available. Your other draft changes are kept.
                  Return to the process map to inspect the current paths.
                </p>
                <div
                  class="workflow-selection-heading"
                  [class.workflow-selection-heading--step]="
                    selectedMapNode()?.typeCode === 'human_step' &&
                    !isAddingStep()
                  "
                >
                  <header class="workflow-map-inspector__header">
                  <button uiButton type="button" *ngIf="canReturnToDecisionInventory()" [disabled]="isDraftEditorInteractionBlocked()"
                    (click)="returnToDecisionInventory()"><i class="pi pi-arrow-left" aria-hidden="true"></i> Back to Decisions</button>
                  <button uiButton type="button" *ngIf="stepEditorNodeId() !== null || editingRouteSelection() !== null"
                    [disabled]="isDraftEditorInteractionBlocked()" (click)="backToProcessMap()" aria-label="Back to process map" title="Back to process map">
                    <i class="pi pi-arrow-left" aria-hidden="true"></i> Back
                  </button>
                    <div>
                      <small *ngIf="mapSelection() || isAddingStep()" class="workflow-editor-type">{{ mapInspectorTypeLabel() }}</small>

                      <h3
                        #mapInspectorHeading
                        #nodeEditorHeading
                        id="workflow-map-inspector-title"
                        tabindex="-1"
                      >
                        <ng-container *ngIf="!inlineStepNameEditing(); else inlineStepName">
                          <button
                            #renameStepButton
                            *ngIf="canInlineRenameStep(); else staticStepName"
                            uiButton
                            [text]="true"
                            [outlined]="false"
                            type="button"
                            class="workflow-step-name-button"
                            [wrap]="true"
                            aria-label="Rename step"
                            title="Rename step"
                            (click)="beginInlineStepName()"
                          >
                            {{ mapInspectorTitle() }}
                            <i class="pi pi-pencil" aria-hidden="true"></i>
                          </button>
                          <ng-template #staticStepName>{{ mapInspectorTitle() }}</ng-template>
                        </ng-container>
                        <ng-template #inlineStepName>
                          <span class="workflow-step-name-editor" (focusout)="onInlineStepNameFocusOut($event)">
                            <input
                              #inlineStepNameInput
                              uiControl
                              aria-label="Step name"
                              maxlength="160"
                              [ngModel]="nodeFormValues.displayName"
                              (ngModelChange)="changeInlineStepName($event)"
                              [attr.aria-invalid]="inlineStepNameError() ? 'true' : null"
                              [attr.aria-describedby]="inlineStepNameError() ? 'workflow-step-name-error' : null"
                              [disabled]="!canInlineRenameStep()"
                              (keydown.enter)="$event.preventDefault(); finishInlineStepName(true)"
                              (keydown.escape)="$event.preventDefault(); $event.stopPropagation(); cancelInlineStepName()"
                            />
                            <button uiButton type="button" aria-label="Apply step name" title="Apply step name"
                              [disabled]="!canInlineRenameStep()" (click)="finishInlineStepName(true)">
                              <i class="pi pi-check" aria-hidden="true"></i>
                            </button>
                            <button uiButton type="button" aria-label="Cancel rename" title="Cancel rename" (click)="cancelInlineStepName()">
                              <i class="pi pi-times" aria-hidden="true"></i>
                            </button>
                          </span>
                        </ng-template>
                      </h3>
                      <p *ngIf="inlineStepNameError()" id="workflow-step-name-error" role="alert" class="workflow-step-name-error">
                        {{ inlineStepNameError() }}
                      </p>
                      <div
                        *ngIf="nodeFormDirty()"
                        class="workflow-map-inspector__step-flags"
                      >
                        <span
                          *ngIf="nodeFormDirty()"
                          class="workflow-drafts__unapplied-label"
                          role="status"
                          aria-live="polite"
                        >
                          Pending step edits
                        </span>
                      </div>
                    </div>
                  </header>

                  <details
                    #stepActions
                    *ngIf="
                      canAuthor() &&
                      !isAddingStep() &&
                      mapSelection()?.isAuthorable
                    "
                    (keydown.escape)="closeContextActions(stepActions)"
                    class="workflow-context-actions workflow-step-placement-actions"
                  >
                    <summary>
                      {{
                        mapSelection()?.kind === "edge"
                          ? "Path actions"
                          : "Step actions"
                      }}
                    </summary>
                    <div class="workflow-context-actions__items">
                      <button uiButton type="button" [disabled]="isDraftEditorInteractionBlocked()"
                        (click)="closeContextActions(stepActions); openDecisionInventory()">Open Decisions</button>
                      <ng-container
                        *ngIf="selectedMapNode()?.typeCode === 'human_step'"
                      >
                        <button
                          uiButton
                          type="button"
                          [disabled]="isDraftEditorInteractionBlocked()"
                          (click)="
                            beginAddStepBefore(); stepActions.open = false
                          "
                        >
                          Add step before
                        </button>
                        <button
                          uiButton
                          type="button"
                          [disabled]="isDraftEditorInteractionBlocked()"
                          (click)="
                            beginAddStepAfter(); stepActions.open = false
                          "
                        >
                          Add step after
                        </button>
                      </ng-container>
                      <button
                        uiButton
                        *ngIf="selectedMapNode()?.typeCode === 'human_step' || selectedMapNode()?.typeCode === 'terminal'"
                        type="button"
                        [disabled]="!canUndoBehavior()"
                        (click)="undoBehavior(); stepActions.open = false"
                        aria-label="Undo behavior change"
                      >
                        Undo last change
                      </button>
                      <button
                        uiButton
                        *ngIf="mapSelection()?.kind === 'edge'"
                        type="button"
                        [disabled]="isDraftEditorInteractionBlocked()"
                        (click)="
                          beginInsertStepOnSelectedPath();
                          stepActions.open = false
                        "
                      >
                        Insert step on this path
                      </button>
                    </div>
                  </details>
                </div>
                <details
                  *ngIf="selectedStepIssueGroup() as group"
                  #stepIssuesDisclosure
                  class="workflow-step-issues"
                  [hidden]="!group.issues.length"
                  aria-labelledby="workflow-step-issues-heading"
                  data-testid="step-issues"
                >
                  <summary id="workflow-step-issues-heading" #stepIssuesHeading>
                    {{ group.issues.length }}
                    {{ group.issues.length === 1 ? "issue" : "issues" }} ·
                    {{ group.step.label }}
                  </summary>
                  <p
                    *ngIf="
                      selectedStepDiagnostics().length &&
                      hasOpenDraftUnsavedChanges()
                    "
                    class="workflow-step-issues__stale"
                  >
                    Saved validation is stale for local changes. Save and
                    validate again to refresh it.
                  </p>
                  <ul>
                    <li
                      *ngFor="let issue of group.issues"
                      [attr.data-issue-source]="issue.source"
                    >
                      <ng-container *ngIf="issue.source === 'local'">
                        <small>Local setup · not validation</small>
                        <strong>{{ issue.finding.label }}</strong>
                        <p>{{ issue.finding.detail }}</p>
                        <button
                          uiButton
                          [text]="true"
                          [outlined]="false"
                          type="button"
                          [disabled]="isDraftEditorInteractionBlocked()"
                          (click)="openLocalStepIssue(issue.finding)"
                        >
                          {{ localIssueFocusLabel(issue.finding) }}
                        </button>
                      </ng-container>
                      <ng-container *ngIf="issue.source === 'saved'">
                        <small
                          >Saved revision {{ draft.revision }} ·
                          {{ issue.diagnostic.severityCode | titlecase }}</small
                        >
                        <strong>{{
                          diagnosticAreaLabel(issue.diagnostic)
                        }}</strong>
                        <p>{{ issue.diagnostic.message }}</p>
                        <button
                          uiButton
                          [text]="true"
                          [outlined]="false"
                          type="button"
                          [disabled]="isDraftEditorInteractionBlocked()"
                          (click)="openSavedStepIssue(issue.diagnostic)"
                        >
                          {{
                            canFocusDiagnostic(issue.diagnostic)
                              ? diagnosticFocusLabel(issue.diagnostic)
                              : "Open Review section"
                          }}
                        </button>
                      </ng-container>
                    </li>
                  </ul>
                </details>
                <ui-tab-group
                  class="workflow-step-scope-tabs"
                  [stretchTabs]="false"
                  [navigationTabs]="stepScopeTabs()"
                  [activeTabId]="activeStepScopeTab()"
                  navigationAriaLabel="Step sections"
                  (tabRequested)="selectStepScopeTab($event)"
                >
                  <ng-template #stepRoleAssignment>
                    <phms-workflow-assignment-policy
                      #stepAssignmentPolicyEditor
                      *ngIf="isHumanStepOwnerInitialized('behavior', 'readiness')"
                      [compact]="true"
                      [dashboardAssignment]="true"
                      [stepNodeId]="selectedMapNode()?.id || null"
                      [document]="workingDocument()"
                      [latestDocument]="latestAssignmentDocument"
                      [ownerContext]="stepAssignmentOwnerContext"
                      [canAuthor]="canAuthor()"
                      [interactionBlocked]="isDraftEditorInteractionBlocked()"
                      [roleCodes]="authoringReferences()?.roleCodes || []"
                      [queueCodes]="authoringReferences()?.queueCodes || []"
                      [referenceLoading]="isLoadingAuthoringReferences()"
                      [referenceError]="authoringReferencesError()"
                      [resetVersion]="structuredEditorResetVersion()"
                      (documentChange)="onBehaviorDocumentChange($event)"
                      (pendingChangesChange)="
                        onStepAssignmentPolicyPendingChangesChange($event)
                      "
                      (retryReferencesRequested)="retryAuthoringReferences()"
                    />
                  </ng-template>
                  <ng-container *ngFor="let pageScope of presentationOwnerScopes">
                  <phms-workflow-presentation-editor
                    #stepPageEditor
                    [resetVersion]="structuredEditorResetVersion()"
                    [focusBlock]="diagnosticBlockFocus()"
                    [responsibilitySummary]="behaviorResponsibilitySummary()"
                    [workEditorAvailable]="true"
                    [latestDocument]="latestAssignmentDocument"
                    [ownerContext]="stepAssignmentOwnerContext"
                    [formViewContext]="workFormViewContext"
                    [guardPageSettings]="pageScope === 'detail'"
                    [pageSettingsView]="pageSettingsView"
                    [pageSettingsViewState]="pageSettingsView()"
                    [managedDashboardRemoval]="pageScope === 'dashboard'"
                    [managedDashboardSources]="pageScope === 'dashboard'"
                    [managedDashboardElements]="pageScope === 'dashboard'"
                    [managedDashboardSettings]="pageScope === 'dashboard'"
                    [dashboardSettingsView]="dashboardSettingsView"
                    [dashboardSettingsViewState]="dashboardSettingsView()"
                    [dashboardNameSettlement]="dashboardNameSettlement"
                    (dashboardNameOwnedDocumentChange)="onDashboardNameOwnedDocumentChange($event)"
                    [dashboardElementView]="dashboardElementView"
                    [dashboardElementViewState]="dashboardElementView()"
                    (dashboardElementOwnedDocumentChange)="onDashboardElementOwnedDocumentChange($event)"
                    [dashboardSourceView]="dashboardSourceView"
                    [dashboardSourceViewState]="dashboardSourceView()"
                    (dashboardSourceOwnedDocumentChange)="onDashboardSourceOwnedDocumentChange($event)"
                    [dashboardRemovalView]="dashboardRemovalView"
                    [dashboardRemovalViewState]="dashboardRemovalView()"
                    (dashboardRemovalRequested)="onDashboardRemovalRequested($event)"
                    [beforeSelectionChange]="guardBehaviorSelection"
                    [focusResponsibility]="pageScope === 'dashboard' && behaviorResponsibilityFocus()?.dashboardId !== undefined ? behaviorResponsibilityFocus() : null"
                    [roleCodes]="authoringReferences()?.roleCodes || []"
                    [roleCatalogAvailable]="
                      !!authoringReferences() &&
                      !isLoadingAuthoringReferences() &&
                      !authoringReferencesError()
                    "
                    [queueChangesPending]="stepAssignmentPolicyFormDirty()"
                    [canUndoDashboard]="canUndoBehavior()"
                    (undoDashboard)="undoBehavior()"
                    *ngIf="isHumanStepOwnerInitialized(pageScope)"
                    [scope]="pageScope"
                    [active]="isHumanStepOwnerActive(pageScope)"
                    [hidden]="!isHumanStepOwnerActive(pageScope)"
                    [document]="workingDocument()"
                    [node]="selectedMapNode()"
                    [disabled]="
                      !canAuthor() ||
                      isDraftEditorInteractionBlocked() ||
                      nodeFormDirty() ||
                      edgeFormDirty() ||
                      transitionFormDirty() ||
                      decisionFormDirty() ||
                      notificationFormDirty() ||
                      assignmentPolicyFormDirty()
                    "
                    (pendingChangesChange)="
                      onPageEditorPendingChangesChange(pageScope, $event)
                    "
                    (openStep)="openStepPageSetup($event)"
                    (openPaths)="openStepPaths()"
                    (openBehavior)="selectStepWorkspaceScope('behavior')"
                    (assignmentRequested)="openStepAssignment()"
                    (noticeRequirementRequested)="onNoticeRequirementRequested(stepPageEditor, $event)"
                    (openActions)="openStepActions($event)"
                    (openForm)="openBehaviorForm($event)"
                    (openOutcomes)="openStepOutcomes($event)"
                    (documentChange)="
                      pageScope === 'dashboard'
                        ? onBehaviorDocumentChange($event)
                        : onPresentationDocumentChange($event)
                    "
                  />
                  </ng-container>
                  <ui-tab-group
                    class="workflow-behavior-section-tabs"
                    [stretchTabs]="false"
                    [hidden]="
                      selectedMapNode()?.typeCode === 'human_step' &&
                      !isAddingStep() &&
                      stepWorkspaceScope() !== 'behavior'
                    "
                    [navigationTabs]="
                      stepScopeTabs().length || (!mapSelection() && mapInspectorPane() === 'overview') ? [] : inspectorTabs()
                    "
                    [activeTabId]="
                      selectedMapNode()?.typeCode === 'human_step' &&
                      mapContextSupportsBehavior()
                        ? activeBehaviorTab()
                        : mapInspectorPane()
                    "
                    [navigationAriaLabel]="
                      selectedMapNode()?.typeCode === 'human_step' &&
                      mapContextSupportsBehavior()
                        ? 'Step behavior views'
                        : 'Inspector views'
                    "
                    (tabRequested)="selectInspectorTab($event)"
                  >
                    <ui-tab-group
                      class="workflow-advanced-section-tabs"
                      [stretchTabs]="false"
                      [navigationTabs]="[]"
                      [activeTabId]="behaviorSection()"
                      navigationAriaLabel="Step content"
                      (tabRequested)="selectInspectorTab($event)"
                    >
                      <label
                        class="workflow-step-settings-picker"
                        *ngIf="selectedMapNode()?.typeCode === 'human_step' && activeStepScopeTab() === 'settings' && stepScopeTabs().length"
                      >
                        Settings
                        <select
                          uiControl
                          aria-label="Step settings"
                          [ngModel]="behaviorSection()"
                          (ngModelChange)="selectInspectorTab($event)"
                          [disabled]="isDraftEditorInteractionBlocked()"
                        >
                          <option *ngFor="let section of advancedBehaviorTabs()" [value]="section.id">
                            {{ section.label }}
                          </option>
                        </select>
                      </label>
                      <div
                        class="workflow-routing-tools"
                        *ngIf="selectedMapNode()?.typeCode === 'human_step' && !isAddingStep() && stepWorkspaceScope() === 'behavior' && behaviorSection() === 'routes'"
                      >
                        <button uiButton type="button"
                          [disabled]="isDraftEditorInteractionBlocked()"
                          (click)="selectBehaviorSection('transitions')">
                          Progress and step labels
                        </button>
                        <button uiButton type="button" aria-label="Undo last routing or status change"
                          [disabled]="!canUndoBehavior()" (click)="undoBehavior()">
                          <i class="pi pi-undo" aria-hidden="true"></i> Undo
                        </button>
                      </div>
                      <phms-workflow-presentation-editor
                        *ngIf="
                          isHumanStepOwnerInitialized('behavior', 'routes')
                        "
                        [resetVersion]="structuredEditorResetVersion()"
                        [active]="isHumanStepOwnerActive('behavior', 'routes') && mapInspectorPane() !== 'path'"
                        [hidden]="
                          behaviorSection() !== 'routes' ||
                          mapInspectorPane() === 'path'
                        "
                        scope="behavior"
                        [immediateRoutingChanges]="true"
                        [latestDocument]="latestAssignmentDocument"
                        [ownerContext]="stepAssignmentOwnerContext"
                        [formViewContext]="workFormViewContext"
                        [document]="workingDocument()"
                        [node]="selectedMapNode()"
                        [focusAction]="stepActionFocus()"
                        [disabled]="
                          !canAuthor() ||
                          isDraftEditorInteractionBlocked() ||
                          nodeFormDirty() ||
                          edgeFormDirty() ||
                          transitionFormDirty() ||
                          decisionFormDirty() ||
                          notificationFormDirty() ||
                          assignmentPolicyFormDirty()
                        "
                        (pendingChangesChange)="reviewRoutingDirty.set($event)"
                        (openStep)="openStepPageSetup($event)"
                        (openPaths)="openStepPaths()"
                        (openOutcomePath)="openHumanOutcomePath($event)"
                        (openDirectPath)="openHumanDirectPath()"
                        (openRequirements)="openStepRoutingRequirements()"
                        [externalWorkflowStatuses]="true"
                        [workflowStatusesView]="workflowStatusesView"
                        (workflowStatusesOwnerRequested)="onWorkflowStatusesOwnerRequested($event)"
                        (openForm)="openBehaviorForm($event)"
                        (openActions)="openStepActions($event)"
                        (openOutcomes)="openStepOutcomes($event)"
                        (documentChange)="onBehaviorDocumentChange($event)"
                      />

                      <div
                        class="workflow-map-inspector__body"
                        [hidden]="
                          selectedMapNode()?.typeCode === 'human_step' &&
                          !isAddingStep() &&
                          (stepWorkspaceScope() !== 'behavior' ||
                            (behaviorSection() === 'routes' &&
                              mapInspectorPane() !== 'path'))
                        "
                      >
                        <section
                          class="workflow-map-inspector__pane"
                          [hidden]="
                            selectedMapNode()?.typeCode === 'human_step' &&
                            !isAddingStep()
                              ? behaviorSection() !== 'details'
                              : mapInspectorPane() !== 'overview'
                          "
                          aria-labelledby="workflow-map-inspector-title"
                        >
                          <div
                            *ngIf="!mapSelection() && !isAddingStep()"
                            class="workflow-map-inspector__workflow-overview"
                            data-testid="workflow-overview"
                          >
                            <section class="workflow-overview__summary-section">
                              <h5>Purpose</h5>
                              <p>{{ workingDocument()?.description || 'No workflow description recorded.' }}</p>
                              <button uiButton [text]="true" [outlined]="false" type="button" [disabled]="isDraftEditorInteractionBlocked()"
                                (click)="openWorkflowSettings('general')">General settings</button>
                            </section>
                            <section class="workflow-overview__summary-section">
                              <h5>Starts with</h5>
                              <div *ngFor="let entry of designerStartEntries()"><strong>{{ entry.label }}</strong><p>{{ entry.detail }}</p></div>
                              <p *ngIf="!designerStartEntries().length">No entry point recorded.</p>
                              <p class="workflow-overview__counts">{{ humanStepNodes().length }} staff {{ humanStepNodes().length === 1 ? 'task' : 'tasks' }} · {{ automatedStepNodes().length }} automated · {{ designerEndCount() }} end {{ designerEndCount() === 1 ? 'step' : 'steps' }} · {{ routingPathCount() }} {{ routingPathCount() === 1 ? 'path' : 'paths' }}</p>
                            </section>
                            <section class="workflow-overview__summary-section" *ngIf="designerIssueGroups().length || workflowOverview().workflowAttention.length">
                              <h5>Needs attention</h5>
                              <ul class="workflow-overview__issue-list">
                                <li *ngFor="let group of designerIssueGroups()">
                                  <button uiButton [text]="true" [outlined]="false" [wrap]="true" type="button" [disabled]="isDraftEditorInteractionBlocked()"
                                    (click)="openStepIssues(group.step)">{{ group.step.label }} · {{ group.issues.length }} {{ group.issues.length === 1 ? 'issue' : 'issues' }}</button>
                                </li>
                                <li *ngFor="let item of workflowOverview().workflowAttention"><strong>{{ item.label }}</strong><p>{{ item.detail }}</p>
                                  <button uiButton [text]="true" [outlined]="false" type="button" [disabled]="isDraftEditorInteractionBlocked()"
                                    (click)="openWorkflowSettings('technical')">Inspect retained configuration</button>
                                </li>
                              </ul>
                            </section>
                            <div class="workflow-overview__summary-actions" role="group" aria-label="Workflow configuration">
                              <button uiButton type="button" [disabled]="isDraftEditorInteractionBlocked()" (click)="openDecisionInventory()">Decisions</button>
                              <button uiButton type="button" [disabled]="isDraftEditorInteractionBlocked()" (click)="openWorkflowSettings()">Workflow settings</button>
                              <button uiButton type="button" [disabled]="isDraftEditorInteractionBlocked()" (click)="selectWorkbenchView('review')">Review saved checks</button>
                            </div>
                            <details class="workflow-overview__map-actions">
                              <summary>Map &amp; workflow rules</summary>
                              <p>{{ workflowStatusChangeCount() }} lifecycle changes · {{ workflowDecisionRuleCount() }} decision rules</p>
                              <div class="workflow-overview__summary-actions">
                                <button uiButton type="button" [disabled]="isDraftEditorInteractionBlocked()" (click)="selectMapInspectorPane('decisions')">{{ canAuthor() ? 'Configure' : 'View' }} decision rules</button>
                                <ng-container *ngIf="canAuthor()">
                                  <button uiButton type="button" [disabled]="isDraftEditorInteractionBlocked()" (click)="beginAddMapPath()">Add map path</button>
                                  <button uiButton type="button" [disabled]="isDraftEditorInteractionBlocked()" (click)="beginAddWorkflowStatusChange()">Add lifecycle change</button>
                                  <button uiButton type="button" [disabled]="isDraftEditorInteractionBlocked()" (click)="beginAddWorkflowDecisionRule()">Add decision rule</button>
                                </ng-container>
                              </div>
                            </details>
                          </div>

                          <div
                            *ngIf="
                              mapSelection()?.kind === 'node' &&
                              !mapSelection()?.isAuthorable
                            "
                            class="workflow-map-inspector__invalid-evidence"
                          >
                            <strong>This node needs validation repair</strong>
                            <p>
                              Its saved identity is missing, duplicated, or
                              incomplete. It remains selectable as evidence, but
                              contextual editing is disabled so another node is
                              not changed accidentally.
                            </p>
                            <code
                              >nodes[{{
                                mapSelection()?.sourceIndex ?? "unresolved"
                              }}]</code
                            >
                          </div>

                          <div
                            [hidden]="
                              selectedMapNode()?.typeCode === 'human_step' &&
                              !isAddingStep() &&
                              behaviorSection() !== 'details'
                            "
                          >
                            <ng-container
                              *ngIf="
                                isAddingStep() ||
                                (mapSelection()?.kind === 'node' &&
                                  mapSelection()?.isAuthorable &&
                                  (isStepEditorActive() || isAutomatedStepOwnerInitialized() ||
                                    !['human_step', 'automated_step', 'terminal'].includes(selectedMapNode()?.typeCode || '')))
                              "
                              [ngTemplateOutlet]="workflowStepInspector"
                            ></ng-container>
                          </div>
                          <phms-workflow-step-child-work-editor
                            *ngIf="activeWorkbenchView() === 'map' && selectedMapNode()?.typeCode === 'terminal' && isStepEditorActive() && mapSelection()?.isAuthorable && !isAddingStep()"
                            [document]="workingDocument()" [nodeId]="selectedMapNode()?.id ?? null" [canAuthor]="canAuthor()"
                            [workspaceMode]="true" [latestDocument]="latestAssignmentDocument" [ownerContext]="stepAssignmentOwnerContext"
                            [viewContext]="workFormViewContext" [active]="isChildWorkOwnerActive"
                            [activeView]="childWorkOwnerView" [activeViewState]="childWorkOwnerView()"
                            [interactionBlocked]="childWorkInteractionBlocked()"
                            [resetVersion]="structuredEditorResetVersion()"
                            (documentChange)="onChildWorkDocumentChange($event)"
                            (ownedDocumentChange)="onChildWorkOwnedDocumentChange($event)"
                            (pendingChangesChange)="
                              childWorkFormDirty.set($event)
                            "
                          />
                          <ui-panel
                            *ngIf="
                              selectedMapNode()?.typeCode === 'human_step' &&
                              !isAddingStep()
                            "
                            class="workflow-step-assignment-panel"
                            [hidden]="
                              selectedMapNode()?.typeCode !== 'human_step' ||
                              isAddingStep() ||
                              stepWorkspaceScope() !== 'behavior' ||
                              behaviorSection() !== 'details'
                            "
                            panelTitle="Assignment"
                            [noGrow]="true"
                            aria-label="Assignment settings location"
                            data-testid="step-responsibility"
                          >
                            <p>
                              Choose who can receive this step's work in Work.
                            </p>
                            <button
                              uiButton
                              type="button"
                              [disabled]="isDraftEditorInteractionBlocked()"
                              (click)="openStepAssignment()"
                            >
                              Edit assignment in Work
                            </button>
                          </ui-panel>
                        </section>

                        <section
                          class="workflow-map-inspector__pane"
                          [hidden]="
                            mapInspectorPane() !== 'path' ||
                            (editingRouteSelection() !== null && focusedRouteIndex() === null) ||
                            (selectedMapNode()?.typeCode === 'human_step' &&
                              behaviorSection() !== 'routes')
                          "
                          aria-label="Map path editor"
                        >
                          <phms-platform-workflow-draft-edge-editor
                            [contextual]="true"
                            [document]="workingDocument()"
                            [canAuthor]="canAuthor()"
                            [interactionBlocked]="
                              isDraftEditorInteractionBlocked() || isInspectingRoute() ||
                              (editingRouteSelection() !== null && focusedRouteIndex() === null) ||
                              (mapSelection()?.kind === 'edge' &&
                                !mapSelection()?.isAuthorable)
                            "
                            [resetContextEdgeIndex]="
                              editingRouteSelection() ? focusedRouteIndex() :
                                mapSelection()?.kind === 'edge' ? mapSelection()?.sourceIndex : null
                            "
                            [resetVersion]="structuredEditorResetVersion()"
                            (documentChange)="onRoutingDocumentChange($event)"
                            (pendingChangesChange)="
                              onEdgePendingChangesChange($event)
                            "
                          ></phms-platform-workflow-draft-edge-editor>
                        </section>

                        <section
                          class="workflow-map-inspector__pane"
                          [hidden]="
                            selectedMapNode()?.typeCode === 'human_step'
                              ? behaviorSection() !== 'transitions' ||
                                mapInspectorPane() === 'decisions'
                              : mapInspectorPane() !== 'transitions'
                          "
                          aria-label="Status change editor"
                        >
                          <div
                            *ngIf="canReturnToNotifications()"
                            class="workflow-status-rules__return"
                          >
                            <button
                              uiButton
                              [text]="true"
                              [outlined]="false"
                              type="button"
                              class="workflow-drafts__text-button"
                              data-testid="back-to-notifications"
                              [disabled]="isDraftEditorInteractionBlocked()"
                              (click)="returnToNotifications()"
                            >
                              <i
                                class="pi pi-arrow-left"
                                aria-hidden="true"
                              ></i>
                              Back to notifications
                            </button>
                            <span>{{
                              selectedMapNode()?.displayName ||
                                selectedMapNode()?.stepCode
                            }}</span>
                          </div>
                          <phms-platform-workflow-draft-transition-editor
                            [contextual]="true"
                            [document]="workingDocument()"
                            [canAuthor]="canAuthor()"
                            [interactionBlocked]="
                              isDraftEditorInteractionBlocked() ||
                              decisionFormDirty() ||
                              notificationFormDirty()
                            "
                            [resetContextStepNodeId]="
                              selectedMapNode()?.typeCode === 'human_step'
                                ? selectedMapNode()?.id
                                : null
                            "
                            [resetVersion]="structuredEditorResetVersion()"
                            [stateCodes]="
                              authoringReferences()?.stateCodes || []
                            "
                            [routingContracts]="
                              authoringReferences()?.routingContracts || []
                            "
                            [roleCodes]="authoringReferences()?.roleCodes || []"
                            [queueCodes]="
                              authoringReferences()?.queueCodes || []
                            "
                            [managerNotificationTargetCodes]="
                              authoringReferences()
                                ?.managerNotificationTargetCodes || []
                            "
                            [externalNotificationTargetCodes]="
                              authoringReferences()
                                ?.externalNotificationTargetCodes || []
                            "
                            [referenceLoading]="isLoadingAuthoringReferences()"
                            [referenceError]="authoringReferencesError()"
                            (documentChange)="onRoutingDocumentChange($event)"
                            (pendingChangesChange)="
                              onTransitionPendingChangesChange($event)
                            "
                            (decisionRulesRequested)="
                              openDecisionRulesForTransition($event)
                            "
                            (stepDecisionRulesRequested)="
                              openStatusRuleDecisionChecks($event)
                            "
                            (completionRequirementsRequested)="
                              openStatusRuleRequirements($event)
                            "
                            [externalWorkflowStatuses]="true"
                            [stepStatusesWorkspace]="selectedMapNode()?.typeCode === 'human_step'"
                            [requireProgressOwnerAcknowledgement]="true"
                            (progressOwnedDocumentChange)="onStepProgressOwnedDocumentChange($event)"
                            (bypassDataOwnerRequested)="onBypassDataRequested($event)"
                            [viewContext]="workFormViewContext"
                            [latestDocument]="latestAssignmentDocument"
                            [ownerContext]="stepAssignmentOwnerContext"
                            [workflowStatusesView]="workflowStatusesView"
                            [workflowStatusesViewState]="workflowStatusesView()"
                            (workflowStatusesOwnerRequested)="onWorkflowStatusesOwnerRequested($event)"
                            (notificationContextRequested)="
                              openStepNotifications($event)
                            "
                            (retryReferencesRequested)="
                              retryAuthoringReferences()
                            "
                          ></phms-platform-workflow-draft-transition-editor>
                        </section>

                        <section *ngIf="isHumanStepOwnerInitialized('behavior', 'children')" [hidden]="!isHumanStepOwnerActive('behavior', 'children')" class="workflow-map-inspector__pane" aria-label="Related work editor">
                          <phms-workflow-step-child-work-editor
                            [document]="workingDocument()" [nodeId]="selectedMapNode()?.id ?? null" [canAuthor]="canAuthor()"
                            [workspaceMode]="true" [latestDocument]="latestAssignmentDocument" [ownerContext]="stepAssignmentOwnerContext"
                            [viewContext]="workFormViewContext" [active]="isChildWorkOwnerActive"
                            [activeView]="childWorkOwnerView" [activeViewState]="childWorkOwnerView()"
                            [interactionBlocked]="childWorkInteractionBlocked()"
                            [resetVersion]="structuredEditorResetVersion()"
                            (documentChange)="onChildWorkDocumentChange($event)"
                            (ownedDocumentChange)="onChildWorkOwnedDocumentChange($event)"
                            (pendingChangesChange)="
                              childWorkFormDirty.set($event)
                            "
                          />
                        </section>

                        <section *ngIf="isHumanStepOwnerInitialized('behavior', 'timers')" [hidden]="!isHumanStepOwnerActive('behavior', 'timers')"
                          class="workflow-map-inspector__pane" aria-label="Step timer editor">
                          <phms-workflow-step-timers-editor
                            [document]="workingDocument()"
                            [nodeId]="selectedMapNode()?.id ?? null"
                            [canAuthor]="canAuthor()"
                            [optionalStageAuthoring]="true"
                            [workspaceMode]="true" [latestDocument]="latestAssignmentDocument" [ownerContext]="stepAssignmentOwnerContext"
                            [viewContext]="workFormViewContext" [active]="isTimerOwnerActive"
                            [activeView]="timerOwnerView" [activeViewState]="timerOwnerView()"
                            [interactionBlocked]="timerInteractionBlocked()"
                            [resetVersion]="structuredEditorResetVersion()"
                            (documentChange)="onTimerDocumentChange($event)"
                            (ownedDocumentChange)="onTimerOwnedDocumentChange($event)"
                            (pendingChangesChange)="timerFormDirty.set($event)"
                          />
                        </section>

                        <section
                          class="workflow-map-inspector__pane"
                          [hidden]="
                            selectedMapNode()?.typeCode !== 'human_step' ||
                            behaviorSection() !== 'readiness'
                          "
                          aria-label="Step work"
                          data-testid="step-work"
                        >
                          <header class="workflow-work__heading" [hidden]="activeWorkView() === 'overview' || activeWorkView() === 'components'">
                            <button uiButton [size]="undefined" [text]="true" type="button" [disabled]="isDraftEditorInteractionBlocked()" (click)="selectWorkView('overview')">Work overview</button>
                            <h3 #workViewHeading id="workflow-work-view-title" tabindex="-1">{{ workViewTitle() }}</h3>
                          </header>
                          <div class="workflow-work__view workflow-work__overview" data-work-view="overview" [hidden]="activeWorkView() !== 'overview'">
                            <ng-template #assignmentOverviewActions><button uiButton [size]="undefined" [wrap]="true" [text]="true" type="button" class="workflow-work__header-action" [disabled]="isDraftEditorInteractionBlocked()" (click)="openStepAssignment()">Edit assignment</button></ng-template>
                            <ui-panel #workOverviewHeading panelTitle="Who does this work" [headerActionsTemplate]="assignmentOverviewActions" [noGrow]="true" role="group" aria-label="Who does this work" tabindex="-1">
                              <div class="workflow-work__body">
                                <dl class="workflow-work__assignment-summary">
                                  <div *ngIf="workAssignmentSummary().roleLabels.length"><dt>Eligible roles</dt><dd><span *ngFor="let role of workAssignmentSummary().roleLabels">{{ role }}</span></dd></div>
                                  <div *ngIf="workAssignmentSummary().modeLabel"><dt>How work is assigned</dt><dd>{{ workAssignmentSummary().modeLabel }}</dd></div>
                                </dl>
                                <p>{{ workAssignmentSummary().description }}</p>
                                <p *ngIf="workAssignmentSummary().unavailableRoleCodes.length" role="status">Saved role choices are currently unavailable: {{ workAssignmentSummary().unavailableRoleCodes.join(', ') }}. Their settings are retained.</p>
                              </div>
                            </ui-panel>
                          </div>
                          <div class="workflow-work__view" [class.workflow-work__requirements--overview]="activeWorkView() === 'overview'" data-work-view="requirements" [hidden]="activeWorkView() !== 'overview' && activeWorkView() !== 'requirements'">
                          <phms-workflow-step-requirements-editor
                            #completionRequirements
                            *ngIf="isHumanStepOwnerInitialized('behavior', 'readiness')"
                            [overview]="activeWorkView() === 'overview'"
                            [overviewNavigationBlocked]="isDraftEditorInteractionBlocked()"
                            [active]="isHumanStepOwnerActive('behavior', 'readiness') && activeWorkView() === 'requirements'"
                            [document]="workingDocument()"
                            [latestDocument]="latestAssignmentDocument"
                            [ownerContext]="stepAssignmentOwnerContext"
                            [nodeId]="selectedMapNode()?.id ?? null"
                            [canAuthor]="canAuthor()"
                            [interactionBlocked]="
                              isDraftEditorInteractionBlocked() ||
                              transitionFormDirty() ||
                              notificationFormDirty() ||
                              decisionFormDirty()
                            "
                            [resetVersion]="structuredEditorResetVersion()"
                            (documentChange)="onBehaviorDocumentChange($event)"
                            (pendingChangesChange)="
                              requirementFormDirty.set($event)
                            "
                            (editForm)="openBehaviorForm($event)"
                            (openActions)="openStepActions($event)"
                            (openStaffPage)="openRequirementStaffPage()"
                            (manageRequested)="selectWorkView('requirements')"
                          />
                          </div>
                          <p
                            *ngIf="
                              selectedMapNode()?.typeCode === 'human_step' &&
                              stepHasDecisionChecks()
                            "
                            class="workflow-steps__status-check-note"
                            [hidden]="activeWorkView() !== 'overview' && activeWorkView() !== 'requirements'"
                          >
                            This step has recorded-decision checks in Status
                            rules.
                            <button
                              uiButton
                              type="button"
                              [disabled]="isDraftEditorInteractionBlocked()"
                              (click)="openStepDecisionChecks()"
                            >
                              Review status rules
                            </button>
                          </p>
                          <phms-workflow-step-components-editor
                            #workComponentsEditor
                            class="workflow-work__components"
                            [class.workflow-work__components--overview]="activeWorkView() === 'overview'"
                            [hidden]="activeWorkView() !== 'overview' && activeWorkView() !== 'components'"
                            *ngIf="isHumanStepOwnerInitialized('behavior', 'readiness') && workingDocument() && selectedMapNode()"
                            [active]="isHumanStepOwnerActive('behavior', 'readiness') && activeWorkView() === 'components'"
                            [overview]="activeWorkView() === 'overview'"
                            scope="work"
                            [contextualWorkHeader]="true"
                            [document]="workingDocument()!"
                            [latestDocument]="latestAssignmentDocument"
                            [ownerContext]="stepAssignmentOwnerContext"
                            [node]="selectedMapNode()!"
                            [formViewContext]="workFormViewContext"
                            [formLookupOwner]="workFormLookupOwner"
                            [resetVersion]="structuredEditorResetVersion()"
                            [focusForm]="behaviorFormFocus()"
                            [focusAction]="stepActionFocus()"
                            [beforeSelectionChange]="guardBehaviorSelection"
                            [disabled]="!canAuthor() || isDraftEditorInteractionBlocked() || nodeFormDirty() || edgeFormDirty() || transitionFormDirty() || decisionFormDirty() || notificationFormDirty() || assignmentPolicyFormDirty()"
                            (pendingChangesChange)="onWorkComponentsPendingChangesChange($event)"
                            (documentChange)="onBehaviorDocumentChange($event)"
                            (openOutcomes)="openStepOutcomes($event)"
                            (openRequirements)="selectWorkView('requirements')"
                            (openStaffPage)="openRequirementStaffPage()"
                            (workOverviewRequested)="selectWorkView('overview')"
                            (openPaths)="openStepPaths()"
                            (workSectionRequested)="manageWorkComponents($event)"
                          />
                          <section
                            class="workflow-work-assignment workflow-work__view"
                            data-work-view="assignment"
                            [hidden]="activeWorkView() !== 'assignment'"
                            data-testid="step-work-assignment"
                            aria-labelledby="workflow-work-view-title"
                          >
                            <ng-container *ngTemplateOutlet="stepRoleAssignment" />
                          </section>
                        </section>

                        <section
                          class="workflow-map-inspector__pane"
                          [hidden]="
                            selectedMapNode()?.typeCode === 'human_step'
                              ? behaviorSection() !== 'transitions' ||
                                mapInspectorPane() !== 'decisions'
                              : mapInspectorPane() !== 'decisions'
                          "
                          aria-label="Decision rule editor"
                        >
                          <div
                            *ngIf="selectedMapNode()?.typeCode === 'human_step'"
                            class="workflow-status-rules__return"
                          >
                            <button
                              uiButton
                              [text]="true"
                              [outlined]="false"
                              *ngIf="canReturnToNotifications()"
                              type="button"
                              class="workflow-drafts__text-button"
                              data-testid="back-to-notifications"
                              [disabled]="isDraftEditorInteractionBlocked()"
                              (click)="returnToNotifications()"
                            >
                              <i
                                class="pi pi-arrow-left"
                                aria-hidden="true"
                              ></i>
                              Back to notifications
                            </button>
                            <button
                              uiButton
                              [text]="true"
                              [outlined]="false"
                              *ngIf="!canReturnToNotifications()"
                              type="button"
                              class="workflow-drafts__text-button"
                              data-testid="back-to-status-rules"
                              [disabled]="isDraftEditorInteractionBlocked()"
                              (click)="returnToStatusRules()"
                            >
                              <i
                                class="pi pi-arrow-left"
                                aria-hidden="true"
                              ></i>
                              Back to status rules
                            </button>
                            <span>{{
                              selectedMapNode()?.displayName ||
                                selectedMapNode()?.stepCode
                            }}</span>
                          </div>
                          <phms-platform-workflow-draft-decision-editor
                            [contextual]="!decisionInventoryCheckAddMode()"
                            [statusChecksPresentation]="decisionInventoryCheckAddMode() || selectedMapNode()?.typeCode === 'human_step'"
                            [document]="workingDocument()"
                            [canAuthor]="canAuthor()"
                            [interactionBlocked]="
                              isDraftEditorInteractionBlocked() ||
                              transitionFormDirty() ||
                              notificationFormDirty()
                            "
                            [resetContextStepNodeId]="
                              selectedMapNode()?.typeCode === 'human_step'
                                ? selectedMapNode()?.id
                                : null
                            "
                            [resetVersion]="structuredEditorResetVersion()"
                            [decisionKindCodes]="
                              authoringReferences()?.decisionKindCodes || []
                            "
                            [decisionOutcomeCodes]="
                              authoringReferences()?.decisionOutcomeCodes || []
                            "
                            [decisionReactionCodes]="
                              authoringReferences()?.decisionReactionCodes || []
                            "
                            [stateCodes]="
                              authoringReferences()?.stateCodes || []
                            "
                            [roleCodes]="authoringReferences()?.roleCodes || []"
                            [queueCodes]="
                              authoringReferences()?.queueCodes || []
                            "
                            [managerNotificationTargetCodes]="
                              authoringReferences()
                                ?.managerNotificationTargetCodes || []
                            "
                            [externalNotificationTargetCodes]="
                              authoringReferences()
                                ?.externalNotificationTargetCodes || []
                            "
                            [referenceLoading]="isLoadingAuthoringReferences()"
                            [referenceError]="authoringReferencesError()"
                            (documentChange)="onDecisionDocumentChange($event)"
                            (workflowChecksRequested)="
                              openWholeWorkflowDecisionChecks()
                            "
                            (pendingChangesChange)="
                              onDecisionPendingChangesChange($event)
                            "
                            (notificationContextRequested)="
                              openStepNotifications($event)
                            "
                            (retryReferencesRequested)="
                              retryAuthoringReferences()
                            "
                          ></phms-platform-workflow-draft-decision-editor>
                        </section>
                        <section
                          class="workflow-map-inspector__pane"
                          [hidden]="
                            selectedMapNode()?.typeCode !== 'human_step' ||
                            behaviorSection() !== 'notifications'
                          "
                          aria-label="Saved step notifications"
                        >
                          <p class="workflow-designer-note">
                            Saved notification settings are available for
                            inspection. Notification delivery is not available
                            in this version.
                          </p>
                          <phms-workflow-step-notifications
                            [document]="workingDocument()"
                            [nodeId]="
                              selectedMapNode()?.typeCode === 'human_step'
                                ? (selectedMapNode()?.id ?? null)
                                : null
                            "
                            [canAuthor]="false"
                            [interactionBlocked]="
                              isDraftEditorInteractionBlocked() ||
                              transitionFormDirty() ||
                              decisionFormDirty()
                            "
                            [roleCodes]="authoringReferences()?.roleCodes || []"
                            [queueCodes]="
                              authoringReferences()?.queueCodes || []
                            "
                            [managerNotificationTargetCodes]="
                              authoringReferences()
                                ?.managerNotificationTargetCodes || []
                            "
                            [externalNotificationTargetCodes]="
                              authoringReferences()
                                ?.externalNotificationTargetCodes || []
                            "
                            [notificationContextProfiles]="
                              authoringReferences()
                                ?.notificationContextProfiles || []
                            "
                            [messageFocus]="notificationMessageFocus()"
                            [referenceLoading]="isLoadingAuthoringReferences()"
                            [resetVersion]="structuredEditorResetVersion()"
                            (documentChange)="onBehaviorDocumentChange($event)"
                            (pendingChangesChange)="
                              notificationFormDirty.set($event)
                            "
                            (openOwner)="openNotificationOwner($event)"
                          />
                        </section>
                      </div>
                    </ui-tab-group>
                  </ui-tab-group>
                </ui-tab-group>
                </div>
              </div>
            </phms-platform-workflow-draft-graph-preview>
          </section>

          <section class="workflow-workbench__panel" id="workflow-workbench-panel-decision-inventory"
            role="region" aria-label="Decisions workspace" [hidden]="activeWorkbenchView() !== 'decision-inventory'">
            <p *ngIf="decisionInventoryLoading()" role="status">Checking current action information…</p>
            <p *ngIf="decisionInventoryError()" role="status">{{ decisionInventoryError() }}
              <button uiButton type="button" [disabled]="decisionInventoryLoading() || isDraftEditorInteractionBlocked()" (click)="loadDecisionInventoryCatalogs()">Retry action information</button>
            </p>
            <phms-workflow-decision-inventory *ngIf="decisionInventoryInitialized()" [inventory]="decisionInventory()"
              [context]="decisionInventoryContext" [active]="activeWorkbenchView() === 'decision-inventory'"
              [disabled]="isDraftEditorInteractionBlocked()" [canAddCheck]="canAuthor() && !decisionInventoryLoading() && !hasOpaqueDecisionCollections()"
              (backRequested)="backFromDecisionInventory($event)" (ownerRequested)="openDecisionInventoryOwner($event)"
              (addCheckRequested)="addDecisionInventoryCheck($event)" />
          </section>
          <section
            id="workflow-workbench-panel-setup"
            class="workflow-workbench__panel workflow-settings"
            role="region"
            aria-labelledby="workflow-settings-title"
            [hidden]="activeWorkbenchView() !== 'setup'"
          >
            <div class="workflow-workbench__panel-heading">
              <div>
                <h3 #setupHeading id="workflow-settings-title" tabindex="-1">Workflow settings</h3>
                <p>Configure this draft. Use Save draft to keep changes across all settings.</p>
              </div>
            </div>
            <p *ngIf="!canAuthor()" class="workflow-drafts__hint">You have read access to this draft. Author permission is required to change or save it.</p>
            <div class="workflow-settings__layout">
              <ui-panel class="workflow-settings__sidebar" panelTitle="Settings">
                <nav class="workflow-settings__tasks" data-testid="workflow-settings-tasks" aria-label="Workflow settings tasks">
                  <button *ngFor="let task of workflowSettingsTabs()" uiButton
                    [text]="activeWorkflowSettingsTask() !== task.id" [outlined]="false" [wrap]="true"
                    [severity]="activeWorkflowSettingsTask() === task.id ? 'primary' : 'secondary'"
                    type="button" class="workflow-settings__task-link"
                    [class.workflow-settings__task-link--active]="activeWorkflowSettingsTask() === task.id"
                    [attr.aria-current]="activeWorkflowSettingsTask() === task.id ? 'page' : null"
                    [attr.aria-controls]="'workflow-settings-task-' + task.id"
                    [disabled]="task.disabled" (click)="selectWorkflowSettingsTask(task.id)">
                    <span>{{ task.label }}</span>
                    <span *ngIf="task.pending" class="workflow-settings__pending" aria-label="Unapplied input">•</span>
                  </button>
                </nav>
                <a *ngIf="entryPointsVisible" href="/platform-admin/operations" target="_blank" rel="noopener noreferrer"
                  class="workflow-settings__operations">Operations (new tab)</a>
              </ui-panel>
              <div class="workflow-settings__content">
              <section *ngIf="isWorkflowSettingsTaskInitialized('statuses')"
                class="workflow-settings__task" id="workflow-settings-task-statuses" role="region" data-settings-task="statuses"
                [hidden]="activeWorkflowSettingsTask() !== 'statuses'" aria-label="Workflow statuses">
                <button *ngIf="workflowStatusesBackLabel() as backLabel" uiButton type="button" [text]="true"
                  [disabled]="isDraftEditorInteractionBlocked()" (click)="returnToStepStatuses()"
                  data-workflow-status-back>{{ backLabel }}</button>
                <phms-workflow-status-editor #workflowStatusEditor
                  [document]="workingDocument()" [nodeId]="null" [workspaceMode]="true" [immediateChanges]="true"
                  [latestDocument]="latestCapabilityDocument" [ownerContext]="capabilityOwnerContext"
                  [viewContext]="workFormViewContext" [active]="isWorkflowStatusesOwnerActive"
                  [activeView]="workflowStatusOwnerView" [activeViewState]="workflowStatusOwnerView()"
                  [canAuthor]="canAuthor()" [interactionBlocked]="isDraftEditorInteractionBlocked()"
                  [resetVersion]="structuredEditorResetVersion()"
                  (pendingChangesChange)="workflowStatusFormDirty.set($event)"
                  (ownedDocumentChange)="onWorkflowStatusDocumentChange($event)" />
              </section>
              <section class="workflow-settings__task" id="workflow-settings-task-general" role="region" data-settings-task="general"
                [hidden]="activeWorkflowSettingsTask() !== 'general'" aria-labelledby="workflow-settings-general-title">
                <h4 id="workflow-settings-general-title">General</h4>
                <p class="workflow-settings__introduction">Name the process and choose where it can be used.</p>
                <p *ngIf="processNameError()" class="workflow-drafts__hint" role="alert">{{ processNameError() }}</p>
                <p *ngIf="!isProcessFirstDocument()" class="workflow-drafts__hint">
                  Process name: <strong>{{ workingDocument()?.displayName || workingDocument()?.workflowCode }}</strong>.
                  This saved format retains its original name and purpose; editing them requires a supported authoring format.
                </p>
            <section
              class="workflow-drafts__identity"
              aria-labelledby="workflow-draft-identity-title"
            >
              <div class="workflow-drafts__section-heading">
                <div>
                  <h4 id="workflow-draft-identity-title">
                    {{
                      isProcessFirstDocument()
                        ? "About this process"
                        : "Work this workflow manages"
                    }}
                  </h4>
                  <p>
                    These settings apply to the whole workflow. Configure each
                    step's behavior and pages in the Designer.
                  </p>
                </div>
              </div>
              <ui-form
                #identityForm
                instanceId="workflow-setup-basics"
                aria-label="Workflow process details"
                [config]="identityFormConfig"
                [formValues]="identityFormValues"
                [disabled]="!canAuthor() || isDraftEditorInteractionBlocked()"
                (formValuesChange)="onIdentityFormChange($event, 'basics')"
              ></ui-form>
              <p *ngIf="!isProcessFirstDocument()" class="workflow-setup__help">
                The open-workflow limit applies to each subject, such as one
                case or proceeding. This saved version retains its original
                record association.
              </p>
              <p
                *ngIf="
                  !isProcessFirstDocument() && isLoadingAuthoringReferences()
                "
                class="workflow-setup__help"
                role="status"
              >
                Loading record choices. Saved values are retained.
              </p>
              <p
                *ngIf="!isProcessFirstDocument() && authoringReferencesError()"
                class="workflow-setup__help"
                role="status"
              >
                Record choices could not be loaded. You can keep editing; saved
                values are retained.
                <button
                  uiButton
                  [text]="true"
                  [outlined]="false"
                  type="button"
                  class="workflow-drafts__text-button"
                  (click)="retryAuthoringReferences()"
                >
                  Retry choices
                </button>
              </p>
            </section>

            <details
              #identityAvailabilityDetails
              [open]="true"
              class="workflow-setup__disclosure"
              data-testid="workflow-setup-availability"
            >
              <summary>
                Availability
                <span>{{ availabilitySummary(identityFormValues) }}</span>
              </summary>
              <div class="workflow-setup__disclosure-body">
                <p class="workflow-setup__help">
                  Choose where this process may be activated. Access permissions
                  are managed separately.
                </p>
                <ui-form
                  #identityAvailabilityForm
                  instanceId="workflow-setup-availability"
                  aria-label="Process availability"
                  [config]="identityAvailabilityFormConfig"
                  [formValues]="identityFormValues"
                  [disabled]="!canAuthor() || isDraftEditorInteractionBlocked()"
                  (formValuesChange)="
                    onIdentityFormChange($event, 'availability')
                  "
                ></ui-form>
                <p class="workflow-setup__help">
                  {{ scopeKeyGuidance(identityFormValues.scopeTypeCode) }}
                </p>
              </div>
            </details>

                <ui-panel *ngIf="isProcessFirstDocument()" panelTitle="Workflow subject">
                  <div class="workflow-settings__subject">
                  <dl class="workflow-setup__identifiers">
                    <div><dt>Subject</dt><dd>{{ workingDocument()?.subjectTypeCode ? setupCodeLabel(workingDocument()!.subjectTypeCode!) : 'No linked subject' }}</dd></div>
                    <div *ngIf="!canEditProcessCardinality(workingDocument())"><dt>Concurrent process runs</dt><dd>{{ setupCodeLabel(workingDocument()!.instanceCardinalityCode) }}</dd></div>
                  </dl>
                  <p class="workflow-setup__help">The workflow subject owns its lifecycle. Shared record references are configured in Data between steps.</p>
                  <button *ngIf="hearingLifecycleAvailable() || workingDocument()?.subjectTypeCode === 'hearing_cycle'"
                    uiButton [text]="true" type="button" [disabled]="isDraftEditorInteractionBlocked()"
                    (click)="selectWorkflowSettingsTask('hearing')">Open Hearing setup</button>
                  </div>
                </ui-panel>
              </section>
              <section class="workflow-settings__task" id="workflow-settings-task-navigation" role="region" data-settings-task="navigation"
                [hidden]="activeWorkflowSettingsTask() !== 'navigation'" aria-labelledby="workflow-settings-navigation-title">
                <h4 id="workflow-settings-navigation-title">Staff navigation</h4>
                <p class="workflow-settings__introduction">Choose the process icon and links to existing dashboards.</p>
            <phms-workflow-navigation-settings
              *ngIf="isWorkflowSettingsTaskInitialized('navigation')"
              [workspaceMode]="true"
              [latestDocument]="latestAssignmentDocument"
              [ownerContext]="capabilityOwnerContext"
              [viewContext]="workFormViewContext"
              [activeCallback]="isNavigationOwnerActive"
              [activeView]="navigationOwnerView"
              [activeViewState]="navigationOwnerView()"
              [active]="activeWorkbenchView() === 'setup' && activeWorkflowSettingsTask() === 'navigation'"
              [processName]="isProcessFirstDocument() ? identityFormValues.displayName : (workingDocument()?.displayName || workingDocument()?.workflowCode || '')"
              [processNameEditable]="processNameEditable()"
              [processNameError]="processNameError()"
              (processNameChange)="onIdentityFormChange({ displayName: $event }, 'basics')"
              (openStepSetup)="showWholeWorkflowOverview()"
              [document]="workingDocument()!"
              [disabled]="
                !canAuthor() ||
                isDraftEditorInteractionBlocked() ||
                pendingWorkbenchAreaCount() > (navigationFormDirty() ? 1 : 0)
              "
              (pendingChangesChange)="navigationFormDirty.set($event)"
              (documentChange)="onNavigationDocumentChange($event)"
              (ownedDocumentChange)="onNavigationOwnedDocumentChange($event)"
            ></phms-workflow-navigation-settings>

              </section>
              <section class="workflow-settings__task" id="workflow-settings-task-data" role="region" data-settings-task="data"
                [hidden]="activeWorkflowSettingsTask() !== 'data'" aria-labelledby="workflow-settings-data-title">
                <h4 #processDataHeading id="workflow-settings-data-title" tabindex="-1">Data between steps</h4>
                <p class="workflow-settings__introduction">Define shared values and the data required when a run starts. Record references identify records; they do not expose or change record contents.</p>
                <button *ngIf="bypassDataReturnLabel()" type="button" uiButton data-return-step-bypass
                  [disabled]="isDraftEditorInteractionBlocked()" (click)="returnToBypassAfterData()">Back to bypass</button>
                <p *ngIf="!isProcessFirstDocument()" class="workflow-drafts__hint">This saved format does not support editing shared values. Retained content remains unchanged.</p>
                <pre *ngIf="!isProcessFirstDocument() && workingDocument()?.processData">{{ workingDocument()?.processData | json }}</pre>
            <phms-workflow-process-data-editor
              *ngIf="isProcessFirstDocument() && isWorkflowSettingsTaskInitialized('data')"
              [document]="workingDocument()!"
              [workspaceMode]="true"
              [latestDocument]="latestAssignmentDocument"
              [ownerContext]="capabilityOwnerContext"
              [viewContext]="workFormViewContext"
              [active]="isProcessDataOwnerActive"
              [activeView]="processDataOwnerView"
              [activeViewState]="processDataOwnerView()"
              [canAuthor]="canAuthor()"
              [interactionBlocked]="
                isDraftEditorInteractionBlocked() ||
                processDataHasOtherPending()
              "
              (pendingChangesChange)="onProcessDataPendingChangesChange($event)"
              (ownedDocumentChange)="onProcessDataOwnedDocumentChange($event)"
            ></phms-workflow-process-data-editor>

              </section>
              <section class="workflow-settings__task" id="workflow-settings-task-numbering" role="region" data-settings-task="numbering"
                [hidden]="activeWorkflowSettingsTask() !== 'numbering'" aria-labelledby="workflow-settings-numbering-title">
                <h4 id="workflow-settings-numbering-title">Public references</h4>
                <p class="workflow-settings__introduction">Choose whether new runs need a reference visible to staff.</p>
                <div data-testid="workflow-setup-numbering">
                  <phms-workflow-numbering-settings
                    *ngIf="isWorkflowSettingsTaskInitialized('numbering')"
                    [document]="workingDocument()"
                    [latestDocument]="latestNumberingDocument"
                    [savedDraft]="currentDraft()"
                    [issuingLocation]="availabilitySummary(identityFormValues)"
                    [interactionBlocked]="isDraftEditorInteractionBlocked()"
                    [disabled]="!canAuthor() || isDraftEditorInteractionBlocked() || hasUnappliedEditorChanges()"
                    (openGeneralRequested)="openNumberingGeneralSettings()"
                    (documentChange)="onNumberingDocumentChange($event)"
                  />
                </div>
              </section>
              <section class="workflow-settings__task" id="workflow-settings-task-hearing" role="region" data-settings-task="hearing"
                [hidden]="activeWorkflowSettingsTask() !== 'hearing'" aria-labelledby="workflow-settings-hearing-title">
                <h4 id="workflow-settings-hearing-title">Hearing setup</h4>
                <p class="workflow-settings__introduction">Set the workflow's role in a hearing lifecycle.</p>
                <ui-panel panelTitle="Workflow subject" [noGrow]="true" data-testid="workflow-hearing-lifecycle">
                  <div class="workflow-hearing__body">
                    <div class="workflow-hearing__subject">
                      <h5 #hearingLifecycleHeading id="workflow-hearing-lifecycle-title" tabindex="-1">{{ workingDocument()?.subjectTypeCode === 'hearing_cycle' ? 'Hearing' : 'Hearing lifecycle' }}</h5>
                      <span *ngIf="workingDocument()?.subjectTypeCode === 'hearing_cycle'" class="workflow-hearing__configured" role="status">Configured in draft</span>
                    </div>
                    <p>Use this for the main lifecycle of a hearing, including its eligibility calculation. Enrollment links each run to its hearing.</p>
                    <p>Additional work for an existing hearing uses a shared Hearing reference instead. A shared reference does not establish lifecycle ownership.</p>
                    <p *ngIf="workingDocument()?.subjectTypeCode === 'hearing_cycle'; else chooseHearingLifecycle">
                      Hearing is the workflow subject. This authored association does not mean enrollment is active or ready.
                    </p>
                    <ng-template #chooseHearingLifecycle>
                      <p *ngIf="workingDocument()?.subjectTypeCode">
                        Current workflow subject: {{ setupCodeLabel(workingDocument()!.subjectTypeCode!) }}.
                        Choosing Hearing replaces this association for future runs of this version.
                      </p>
                      <button uiButton type="button" (click)="useHearingLifecycle()"
                        [disabled]="!canAuthor() || isDraftEditorInteractionBlocked() || !hearingLifecycleAvailable()">Use Hearing as workflow subject</button>
                      <p *ngIf="!isLoadingAuthoringReferences() && !hearingLifecycleAvailable() && !authoringReferencesError()" role="status">
                        The Hearing subject is not installed in the current record choices.
                        Retry record choices; if it remains unavailable, ask an administrator to check the installed Hearing subject.
                        <button uiButton type="button" [text]="true" (click)="retryAuthoringReferences()"
                          [disabled]="isDraftEditorInteractionBlocked()">Retry record choices</button>
                      </p>
                    </ng-template>
                    <p *ngIf="isLoadingAuthoringReferences()" role="status">Loading Hearing subject availability…</p>
                    <p *ngIf="!isLoadingAuthoringReferences() && authoringReferencesError()" role="status" data-testid="workflow-hearing-reference-error">
                      Record choices could not be refreshed.
                      <span *ngIf="hearingLifecycleAvailable()">The last successfully loaded Hearing subject choice remains available.</span>
                      <span *ngIf="!hearingLifecycleAvailable()">No Hearing subject choice is available from the last successful load.</span>
                      Your draft association and shared values are retained.
                      <button uiButton type="button" [text]="true" (click)="retryAuthoringReferences()"
                        [disabled]="isDraftEditorInteractionBlocked()">Retry record choices</button>
                    </p>
                    <p class="workflow-drafts__hint">Subject changes are local until Save draft. They do not change run limits, remove shared values, configure references, publish or start enrollment.</p>
                    <aside *ngIf="hearingRequiredStartingValues().length" class="workflow-hearing__warning" aria-labelledby="workflow-hearing-starting-title" data-testid="workflow-hearing-starting-values">
                      <div>
                        <h5 id="workflow-hearing-starting-title">Review required starting values</h5>
                        <p>Automatic enrollment cannot supply required starting values. These shared values are currently required at start:</p>
                        <ul>
                          <li *ngFor="let field of hearingRequiredStartingValues()">
                            <span>{{ field.label || field.key }} <code>{{ field.key }}</code></span>
                            <button *ngIf="hearingRequiredStartingValues().length > 1" uiButton [text]="true" type="button"
                              [disabled]="isDraftEditorInteractionBlocked()" (click)="reviewHearingStartingValues(field.key)"
                              [attr.aria-label]="'Review required starting value ' + (field.label || field.key)">Review value</button>
                          </li>
                        </ul>
                        <p>This is draft guidance, not a complete enrollment readiness check. Manual starts may still use required values.</p>
                      </div>
                      <button uiButton type="button" [disabled]="isDraftEditorInteractionBlocked()" (click)="reviewHearingStartingValues()">Review shared values</button>
                    </aside>
                    <p *ngIf="hearingStartingValuesIssue()" role="status">The saved shared-data format cannot be interpreted here. Its original settings are retained; review them in Data between steps.</p>
                    <button *ngIf="!hearingRequiredStartingValues().length" uiButton [text]="true" type="button"
                      [disabled]="isDraftEditorInteractionBlocked()" (click)="reviewHearingStartingValues()">Review shared values</button>
                  </div>
                </ui-panel>
                <ui-panel panelTitle="Automatic enrollment" [noGrow]="true">
                  <div class="workflow-hearing__body">
                    <p>Choose a published workflow version in Operations → Deployment configuration. Enrollment settings have their own Save enrollment draft, Check readiness and Activate enrollment connection actions.</p>
                    <a *ngIf="entryPointsVisible" class="workflow-settings__operations" href="/platform-admin/operations"
                      target="_blank" rel="noopener noreferrer">Open Operations (new tab)</a>
                    <p>Saving or publishing this workflow draft does not save enrollment settings, activate a connection or start enrollment.</p>
                  </div>
                </ui-panel>
              </section>
              <section class="workflow-settings__task" id="workflow-settings-task-automation" role="region" data-settings-task="automation"
                [hidden]="activeWorkflowSettingsTask() !== 'automation'" aria-labelledby="workflow-settings-automation-title">
                <h4 id="workflow-settings-automation-title">Event automation</h4>
                <p class="workflow-settings__introduction">Manage registered event declarations using the existing authoring and execution restrictions.</p>
            <div
              #automationDetails
              data-testid="workflow-setup-automation"
              role="group" aria-label="Event automation editor" tabindex="-1"
            >
                <phms-workflow-automation-editor
                  *ngIf="isWorkflowSettingsTaskInitialized('automation')"
                  [document]="workingDocument()!"
                  [canAuthor]="canAuthor()"
                  [interactionBlocked]="
                    isDraftEditorInteractionBlocked() ||
                    pendingWorkbenchAreaCount() >
                      (automationFormDirty() ? 1 : 0)
                  "
                  [canUndo]="canUndoAutomation()"
                  (undoRequested)="undoAutomation()"
                  (documentChange)="onAutomationDocumentChange($event)"
                  (pendingChangesChange)="automationFormDirty.set($event)"
                />
            </div>

              </section>
              <section class="workflow-settings__task" id="workflow-settings-task-technical" role="region" data-settings-task="technical"
                [hidden]="activeWorkflowSettingsTask() !== 'technical'" aria-labelledby="workflow-settings-technical-title">
                <h4 #technicalHeading id="workflow-settings-technical-title" tabindex="-1">{{ technicalViewTitle() }}</h4>
                <p class="workflow-settings__introduction" *ngIf="activeTechnicalView() === 'identity'">Identify this draft and inspect its installed dependencies.</p>
                <button *ngIf="activeTechnicalView() !== 'identity'" uiButton [size]="undefined" [text]="true" type="button" class="workflow-technical__back"
                  [disabled]="isDraftEditorInteractionBlocked()" (click)="selectTechnicalView('identity')">Back to Technical settings</button>
                <div class="workflow-technical__view" [hidden]="activeTechnicalView() !== 'identity'" data-technical-view="identity">
                  <ui-panel panelTitle="Version identity" [noGrow]="true" data-testid="workflow-setup-technical">
                    <div class="workflow-technical__body">
                      <ui-form #identityTechnicalForm instanceId="workflow-setup-technical" aria-label="Workflow version identity"
                        [config]="identityTechnicalFormConfig" [formValues]="identityFormValues"
                        [disabled]="!canAuthor() || isDraftEditorInteractionBlocked()"
                        (formValuesChange)="onIdentityFormChange($event, 'technical')"></ui-form>
                      <dl class="workflow-technical__identity">
                        <div><dt>Workflow code</dt><dd>{{ identityFormValues.workflowCode }}</dd></div>
                        <div><dt>Payload schema</dt><dd>{{ identityFormValues.payloadSchemaVersion }}</dd></div>
                        <div><dt>Saved draft revision</dt><dd>{{ currentDraft()?.revision }}</dd></div>
                      </dl>
                      <p>Version code identifies the definition you will publish. Saved draft revision changes when the draft is saved; saving does not establish validation or execution readiness.</p>
                      <div *ngIf="['1.0', '1.1', '1.2'].includes(workingDocument()?.payloadSchemaVersion ?? '') && canAuthor()">
                        <button uiButton [size]="undefined" type="button" (click)="enableSavedGraphExecution()" [disabled]="isDraftEditorInteractionBlocked()">Enable editable routing</button>
                        <p>Opt this legacy draft into execution along its saved paths. Save before publishing; existing instances keep their original version.</p>
                      </div>
                    </div>
                  </ui-panel>
                  <ui-panel panelTitle="Inspect and troubleshoot" [noGrow]="true">
                    <div class="workflow-technical__tasks">
                      <div><div><h5>Modules &amp; dependencies</h5><p>Inspect exact references used by the workflow and their catalog availability.</p></div>
                        <button uiButton [size]="undefined" type="button" [disabled]="isDraftEditorInteractionBlocked()" (click)="selectTechnicalView('modules')">Inspect dependencies</button></div>
                      <div><div><h5>Independent assignment policies <span>{{ workingDocument()?.assignmentPolicies?.length || 0 }} retained</span></h5><p>Review existing policies. Step recipients belong in Designer → step → Work → Assignment.</p></div>
                        <button uiButton [size]="undefined" type="button" [disabled]="isDraftEditorInteractionBlocked()" (click)="selectTechnicalView('assignment')">Review policies</button></div>
                      <div><div><h5>Quorum rule calculator</h5><p>Read-only synthetic examples. Calculations do not change this draft.</p></div>
                        <button uiButton [size]="undefined" type="button" [disabled]="isDraftEditorInteractionBlocked()" (click)="selectTechnicalView('quorum')">Open calculator</button></div>
                    </div>
                  </ui-panel>
                </div>
                <div class="workflow-technical__view" [hidden]="activeTechnicalView() !== 'modules'" data-technical-view="modules" data-testid="workflow-capability-inventory">
                  <phms-workflow-capability-picker *ngIf="isTechnicalViewInitialized('modules')" [document]="workingDocument()"
                    [latestDocument]="latestCapabilityDocument"
                    [ownerContext]="capabilityOwnerContext"
                    [disabled]="!canAuthor() || isDraftEditorInteractionBlocked() || hasUnappliedEditorChanges()"
                    (documentChange)="onCapabilityDocumentChange($event)"></phms-workflow-capability-picker>
                  <button uiButton [size]="undefined" type="button" [disabled]="isDraftEditorInteractionBlocked()" (click)="reviewTechnicalValidation()">Review validation</button>
                </div>
                <div class="workflow-technical__view" [hidden]="activeTechnicalView() !== 'assignment'" data-technical-view="assignment" data-testid="workflow-setup-assignment">
                  <p>These retained workflow policies do not become step defaults. Supported legacy queue policies remain editable; role-based policies are inspected here. Step recipients belong in Designer → step → Work → Assignment.</p>
                  <phms-workflow-assignment-policy *ngIf="isTechnicalViewInitialized('assignment')" #workflowAssignmentPolicyEditor
                    [allowNewPolicies]="false" [document]="workingDocument()" [canAuthor]="canAuthor()"
                    [latestDocument]="latestAssignmentDocument" [ownerContext]="capabilityOwnerContext"
                    [interactionBlocked]="isDraftEditorInteractionBlocked() || pendingWorkbenchAreaCount() > (assignmentPolicyFormDirty() ? 1 : 0)"
                    [roleCodes]="authoringReferences()?.roleCodes || []" [queueCodes]="authoringReferences()?.queueCodes || []"
                    [referenceLoading]="isLoadingAuthoringReferences()" [referenceError]="authoringReferencesError()"
                    [resetVersion]="structuredEditorResetVersion()" (documentChange)="onAssignmentPolicyDocumentChange($event)"
                    (pendingChangesChange)="onAssignmentPolicyPendingChangesChange($event)" (retryReferencesRequested)="retryAuthoringReferences()"></phms-workflow-assignment-policy>
                </div>
                <div class="workflow-technical__view" [hidden]="activeTechnicalView() !== 'quorum'" data-technical-view="quorum" data-testid="workflow-setup-quorum-calculator">
                  <phms-workflow-quorum-calculator *ngIf="isTechnicalViewInitialized('quorum')" [disabled]="isDraftEditorInteractionBlocked()"></phms-workflow-quorum-calculator>
                </div>
              </section>
              </div>
            </div>
          </section>
        </div>
      </div>

      <ng-template #workflowStepInspector>
        <section
          class="workflow-steps__workspace workflow-map-inspector__step"
          aria-labelledby="workflow-map-inspector-title"
          data-testid="workflow-step-workspace"
        >
          <section
            *ngIf="isAddingStep()"
            class="workflow-steps__section workflow-step-create"
            aria-labelledby="workflow-new-step-details-title"
          >
            <div class="workflow-steps__section-heading">
              <div>
                <h4 id="workflow-new-step-details-title">
                  Name and place the step
                </h4>
                <p>
                  {{
                    nodeFormValues.typeCode === "automated_step"
                      ? "This automated step has no staff form or assignment queue. Configure its installed activity and outcome routes after adding it."
                      : "Choose where this work belongs. A completion task, staff page, and assignment dashboard are supplied. Choose the responsible roles next."
                  }}
                </p>
              </div>
            </div>
            <ui-form
              #nodeForm
              instanceId="workflow-draft-step-form"
              aria-label="New work step details"
              [config]="nodeFormConfig"
              [(formValues)]="nodeFormValues"
              [disabled]="!canAuthor() || isDraftEditorInteractionBlocked()"
              (formValuesChange)="onNodeFormChange($event)"
            ></ui-form>
            <p
              *ngIf="
                nodeFormValues.typeCode === 'human_step' &&
                standardStepLoading()
              "
              role="status"
            >
              Loading standard task controls…
            </p>
            <div
              *ngIf="
                nodeFormValues.typeCode === 'human_step' && standardStepError()
              "
              role="alert"
            >
              <p>{{ standardStepError() }}</p>
              <button
                uiButton
                type="button"
                (click)="loadStandardStepSupport()"
              >
                Retry task controls
              </button>
            </div>
            <fieldset
              class="workflow-step-create__placement"
              [disabled]="!canAuthor() || isDraftEditorInteractionBlocked()"
            >
              <legend>Where should this step go?</legend>
              <p>{{ newStepPlacementContextLabel }}</p>
              <div class="workflow-step-create__modes">
                <label
                  ><input
                    uiControl
                    type="radio"
                    name="step-placement"
                    value="path"
                    [checked]="newStepPlacementMode === 'path'"
                    (change)="changeNewStepPlacementMode('path')"
                  />
                  Insert on an existing path</label
                >
                <label
                  ><input
                    uiControl
                    type="radio"
                    name="step-placement"
                    value="connection"
                    [checked]="newStepPlacementMode === 'connection'"
                    (change)="changeNewStepPlacementMode('connection')"
                  />
                  Connect an unfinished step</label
                >
                <label
                  ><input
                    uiControl
                    type="radio"
                    name="step-placement"
                    value="unconnected"
                    [checked]="newStepPlacementMode === 'unconnected'"
                    (change)="changeNewStepPlacementMode('unconnected')"
                  />
                  Add unconnected</label
                >
              </div>
              <ng-container *ngIf="newStepPlacementMode === 'path'">
                <label class="workflow-step-create__path-picker"
                  >Insert on path
                  <select
                    uiControl
                    aria-label="Insert on path"
                    [ngModel]="newStepPathId"
                    [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="selectNewStepPath($event)"
                  >
                    <option value="">Choose the exact path</option>
                    <option
                      *ngFor="let path of orderedStepPlacementPaths()"
                      [value]="path.edgeId"
                      [disabled]="!path.eligible"
                    >
                      {{ path.label
                      }}{{ path.eligible ? "" : " — unavailable" }}
                    </option>
                  </select>
                </label>
                <p *ngIf="!newStepPathId" class="workflow-designer-note">
                  Choose the connection to split. When a step has several
                  incoming or outgoing paths, select the branch that should
                  include this work.
                </p>
                <p
                  *ngIf="newStepContextPlacementIssue"
                  class="workflow-designer-note"
                  role="status"
                >
                  {{ newStepContextPlacementIssue }}
                </p>
                <div
                  *ngIf="newStepPlacementStale"
                  class="workflow-step-create__stale"
                  role="alert"
                >
                  <p>
                    This path changed while you were editing. Review the current
                    path before adding your step. Your step details have been
                    kept.
                  </p>
                  <button
                    uiButton
                    type="button"
                    (click)="selectNewStepPath(newStepPathId)"
                  >
                    Review current placement
                  </button>
                </div>
                <details
                  class="workflow-step-create__unavailable"
                  *ngIf="newStepUnavailablePathCount"
                >
                  <summary>
                    Why {{ newStepUnavailablePathCount }}
                    {{
                      newStepUnavailablePathCount === 1
                        ? "path is"
                        : "paths are"
                    }}
                    unavailable
                  </summary>
                  <ul>
                    <ng-container
                      *ngFor="let path of orderedStepPlacementPaths()"
                    >
                      <li
                        *ngIf="!path.eligible"
                        class="workflow-step-create__path-unavailable"
                      >
                        <strong>{{ path.label }}</strong
                        ><span>{{ path.reason }}</span>
                      </li>
                    </ng-container>
                  </ul>
                </details>
                <p
                  *ngIf="!newStepPlacementPaths().length"
                  class="workflow-designer-note"
                >
                  There are no existing paths to split. Connect an unfinished
                  step or add this step unconnected.
                </p>
              </ng-container>
              <div
                *ngIf="newStepPlacementMode === 'connection'"
                class="workflow-step-create__connections"
              >
                <label
                  >Connect from
                  <select
                    uiControl
                    aria-label="Connect from"
                    [value]="newStepFrom"
                    [disabled]="
                      !canAuthor() || isDraftEditorInteractionBlocked()
                    "
                    (change)="
                      changeNewStepConnection('from', $any($event.target).value)
                    "
                  >
                    <option value="">Choose later</option>
                    <option
                      *ngFor="let source of newStepUnfinishedSources()"
                      [value]="source.id"
                    >
                      {{ stepPlacementNodeLabel(source.id) }}
                    </option>
                  </select>
                </label>
                <label
                  >Continue to
                  <select
                    uiControl
                    aria-label="Continue to"
                    [value]="newStepTo"
                    [disabled]="
                      !canAuthor() ||
                      isDraftEditorInteractionBlocked() ||
                      !!newStepInsertionTarget
                    "
                    (change)="
                      changeNewStepConnection('to', $any($event.target).value)
                    "
                  >
                    <option value="">Choose with step behavior</option>
                    <option
                      *ngFor="let target of newStepDestinations()"
                      [value]="target.id"
                    >
                      {{ stepPlacementNodeLabel(target.id) }}
                    </option>
                  </select>
                </label>
              </div>
              <p
                *ngIf="newStepPlacementMode === 'connection'"
                class="workflow-designer-note"
              >
                Connect from a step that has no outgoing path. Choose where the
                new work continues, or configure its next path later.
              </p>
              <p
                *ngIf="newStepPlacementMode === 'unconnected'"
                class="workflow-designer-note"
              >
                This step will be added to the outline. Connect its paths before
                publishing the workflow.
              </p>
              <div
                class="workflow-step-create__preview"
                aria-label="New step placement preview"
                aria-live="polite"
              >
                <span>After adding</span
                ><strong>{{ newStepPlacementPreview }}</strong>
                <small
                  *ngIf="
                    newStepPlacementMode === 'path' && newStepSelectedPathLabel
                  "
                  >Selected path: {{ newStepSelectedPathLabel }}</small
                >
              </div>
            </fieldset>
            <details class="workflow-step-create__advanced">
              <summary>Technical keys and list order</summary>
              <p>
                Keys are suggested from the name. Once added, renaming the step
                keeps its identity.
              </p>
              <div class="workflow-step-create__connections">
                <label
                  >Stable ID<input
                    uiControl
                    aria-label="Stable ID"
                    [value]="nodeFormValues.id"
                    maxlength="120"
                    [disabled]="
                      !canAuthor() || isDraftEditorInteractionBlocked()
                    "
                    (input)="
                      changeNewStepField('id', $any($event.target).value)
                    "
                /></label>
                <label
                  >Step key<input
                    uiControl
                    aria-label="Step key"
                    [value]="nodeFormValues.stepCode"
                    maxlength="120"
                    [disabled]="
                      !canAuthor() || isDraftEditorInteractionBlocked()
                    "
                    (input)="
                      changeNewStepField('stepCode', $any($event.target).value)
                    "
                /></label>
                <label
                  >Initial list order<input
                    uiControl
                    aria-label="Initial list order"
                    type="number"
                    min="0"
                    [value]="nodeFormValues.displayOrder"
                    [disabled]="
                      !canAuthor() ||
                      isDraftEditorInteractionBlocked() ||
                      newStepPlacementMode === 'path'
                    "
                    (input)="
                      changeNewStepField(
                        'displayOrder',
                        $any($event.target).value
                      )
                    "
                /></label>
              </div>
              <p *ngIf="newStepPlacementMode === 'path'">
                List position follows the selected path and is assigned when the
                step is added.
              </p>
            </details>
            <ui-button-group
              [buttonsConfig]="nodeActionButtons()"
              (buttonClick)="onNodeActionButtonClick($event)"
            ></ui-button-group>
          </section>

          <ng-container *ngIf="selectedNode() as node">
            <section
              *ngIf="node.typeCode === 'automated_step' && isAutomatedStepOwnerInitialized()"
              class="workflow-steps__section"
            >
              <phms-workflow-automated-step-editor
                [document]="workingDocument()!"
                [nodeId]="node.id"
                [latestDocument]="latestAssignmentDocument"
                [ownerContext]="stepAssignmentOwnerContext"
                [viewContext]="workFormViewContext"
                [activeView]="activeAutomatedView"
                [section]="activeStepScopeTab()"
                [routeFocus]="isStepEditorActive() ? automatedRouteFocus() : null"
                [canAuthor]="canAuthor()"
                [interactionBlocked]="isDraftEditorInteractionBlocked()"
                (pendingChangesChange)="automatedStepFormDirty.set($event)"
                (documentChange)="onBehaviorDocumentChange($event)"
                (hearingLifecycleSettings)="openHearingLifecycleSettings()"
                (openOutcomePath)="openAutomatedOutcomePath($event)"
                (revealSection)="revealAutomatedPendingSection($event)"
              />
              <div>
                <button
                  uiButton
                  type="button"
                  [disabled]="!canUndoBehavior()"
                  (click)="undoBehavior()"
                >
                  Undo last step change
                </button>
              </div>
            </section>
            <section
              *ngIf="
                !['human_step', 'automated_step'].includes(node.typeCode) &&
                !isAddingStep()
              "
              class="workflow-steps__section workflow-steps__boundary-overview"
              aria-labelledby="workflow-boundary-title"
            >
              <div>
                <h4 id="workflow-boundary-title">{{ nodeTypeLabel(node) }}</h4>
                <p>
                  {{
                    node.typeCode === "entry"
                      ? "This fixed boundary marks where the workflow map begins."
                      : "This fixed boundary marks a completion point in the workflow map."
                  }}
                </p>
              </div>
              <code>{{ node.id }}</code>
            </section>

            <section
              *ngIf="
                ['human_step', 'automated_step'].includes(node.typeCode) &&
                !isAddingStep()
              "
              class="workflow-steps__section workflow-steps__maintenance"
              [hidden]="node.typeCode === 'automated_step' && activeStepScopeTab() !== 'settings'"
              aria-label="Step properties"
            >
              <header>
                <span>
                  <h4>Step properties</h4>
                  <small>Name and position in the step list</small>
                </span>
              </header>
              <div class="workflow-steps__maintenance-content">
                <div class="workflow-steps__maintenance-heading">
                  <p id="workflow-step-order-help">
                    List position controls the step list. Use Outcomes &amp;
                    routing to change where work goes next.
                  </p>
                  <div
                    *ngIf="canAuthor()"
                    class="workflow-steps__order-actions"
                    role="group"
                    aria-label="Change initial work-step order"
                  >
                    <button
                      uiButton
                      [text]="true"
                      [outlined]="false"
                      type="button"
                      class="workflow-drafts__text-button"
                      [attr.aria-label]="
                        'Move ' +
                        (node.displayName || node.stepCode || node.id) +
                        ' earlier'
                      "
                      [disabled]="!canMoveSelectedStepEarlier()"
                      aria-describedby="workflow-step-order-help"
                      (click)="moveSelectedStepEarlier()"
                    >
                      <i class="pi pi-arrow-up" aria-hidden="true"></i>
                      Move earlier
                    </button>
                    <button
                      uiButton
                      [text]="true"
                      [outlined]="false"
                      type="button"
                      class="workflow-drafts__text-button"
                      [attr.aria-label]="
                        'Move ' +
                        (node.displayName || node.stepCode || node.id) +
                        ' later'
                      "
                      [disabled]="!canMoveSelectedStepLater()"
                      aria-describedby="workflow-step-order-help"
                      (click)="moveSelectedStepLater()"
                    >
                      <i class="pi pi-arrow-down" aria-hidden="true"></i>
                      Move later
                    </button>
                  </div>
                </div>

                <ui-form
                  #nodeForm
                  instanceId="workflow-draft-step-form"
                  aria-label="Selected work step details"
                  [config]="nodeFormConfig"
                  [(formValues)]="nodeFormValues"
                  [disabled]="!canAuthor() || isDraftEditorInteractionBlocked()"
                  (formValuesChange)="onNodeFormChange($event)"
                ></ui-form>

                <details
                  #stepTechnicalDetails
                  class="workflow-steps__technical"
                >
                  <summary>Technical details</summary>
                  <label
                    *ngIf="!isInheritedStep(node.id)"
                    class="workflow-step-key"
                    >Step key
                    <input
                      uiControl
                      #stepKeyInput
                      aria-label="Step key"
                      maxlength="120"
                      [ngModel]="nodeFormValues.stepCode"
                      [attr.aria-invalid]="
                        stepKeyValidationError ? 'true' : null
                      "
                      [attr.aria-describedby]="
                        stepKeyValidationError
                          ? 'workflow-step-key-error'
                          : null
                      "
                      (ngModelChange)="
                        onNodeFormChange({
                          stepCode: $event,
                          displayName: nodeFormValues.displayName,
                          displayOrder: nodeFormValues.displayOrder,
                        })
                      "
                      [disabled]="
                        !canAuthor() || isDraftEditorInteractionBlocked()
                      "
                    />
                  </label>
                  <p
                    *ngIf="stepKeyValidationError as error"
                    id="workflow-step-key-error"
                    role="alert"
                  >
                    {{ error }}
                  </p>
                  <dl>
                    <div>
                      <dt>Stable ID</dt>
                      <dd>
                        <code>{{ node.id }}</code>
                      </dd>
                    </div>
                    <div>
                      <dt>Step key</dt>
                      <dd>
                        <code>{{ node.stepCode }}</code>
                      </dd>
                    </div>
                    <div>
                      <dt>Node kind</dt>
                      <dd>
                        <code>{{ node.typeCode }}</code>
                      </dd>
                    </div>
                  </dl>
                  <p *ngIf="isInheritedStep(node.id)">
                    This published compatibility identity cannot be changed or
                    removed in this draft.
                  </p>
                </details>

                <p
                  *ngIf="nodeFormDirty()"
                  class="workflow-drafts__apply-guidance"
                  role="status"
                >
                  Save draft includes these edits once the required fields are
                  complete. Apply updates the local draft without saving.
                </p>
                <ui-button-group
                  [buttonsConfig]="nodeActionButtons()"
                  (buttonClick)="onNodeActionButtonClick($event)"
                ></ui-button-group>
              </div>
            </section>
          </ng-container>
        </section>
      </ng-template>

      <section
        id="workflow-workbench-panel-review"
        class="workflow-workbench__panel"
        role="region"
        aria-labelledby="workflow-workbench-tab-review"
        [hidden]="activeWorkbenchView() !== 'review'"
      >
        <h3 class="visually-hidden">Review and validation</h3>

        <div class="workflow-review">
          <ui-tab-group
            #reviewTabs
            [navigationTabs]="reviewTabsConfig()"
            [activeTabId]="activeReviewView()"
            navigationAriaLabel="Review tasks"
            (tabRequested)="selectReviewTab($event)"
          >
            <section
              id="workflow-review-panel-readiness"
              class="workflow-review__panel"
              role="region"
              aria-labelledby="workflow-review-tab-readiness"
              [hidden]="activeReviewView() !== 'readiness'"
            >
              <section
                class="workflow-review__next"
                data-testid="publication-readiness"
                [attr.data-status]="reviewPublicationReadiness().status"
                aria-labelledby="workflow-review-next-title"
              >
                <div class="workflow-review__summary">
                  <div>
                    <span class="workflow-review__eyebrow"
                      >Saved revision {{ draft.revision }}</span
                    >
                    <h4 id="workflow-review-next-title">
                      {{ reviewPublicationReadiness().label }}
                    </h4>
                    <p>{{ reviewPublicationReadiness().detail }}</p>
                  </div>
                  <button
                    uiButton
                    severity="primary"
                    [text]="false"
                    [outlined]="false"
                    *ngIf="reviewPrimaryActionLabel() as label"
                    type="button"
                    data-testid="review-primary-action"
                    (click)="reviewNextAction()"
                    [disabled]="reviewPrimaryActionDisabled()"
                  >
                    {{ label }}
                  </button>
                </div>
                <phms-workflow-definition-lifecycle-actions
                  #reviewPublicationActions
                  *ngIf="!isArchivedDraft()"
                  publicationLayout="embedded"
                  [draft]="draft"
                  [showPublicationReadiness]="false"
                  [hasUnsavedChanges]="hasOpenDraftUnsavedChanges()"
                  [interactionBlocked]="isDraftEditorInteractionBlocked()"
                  [validationFailed]="automaticValidationFailed()"
                  (operationInProgressChange)="
                    lifecycleOperationInProgress.set($event)
                  "
                  (published)="onDefinitionPublished($event)"
                  (draftValidated)="onPublicationDraftValidated($event)"
                  (publicationChecksFailed)="onPublicationChecksFailed($event)"
                ></phms-workflow-definition-lifecycle-actions>
                <p
                  *ngIf="draft.runtimeBindingCode === 'metadata_only'"
                  class="workflow-review__activation-note"
                >
                  This version can be published after its checks pass, but cannot be
                  activated or start process runs.
                </p>
                <details class="workflow-review__readiness-rail">
                  <summary>Validation and activation details</summary>
                  <dl class="workflow-review__checks">
                    <div
                      [attr.data-status]="
                        hasOpenDraftUnsavedChanges() ? 'attention' : 'complete'
                      "
                    >
                      <dt>
                        <i
                          [class]="
                            hasOpenDraftUnsavedChanges()
                              ? 'pi pi-pencil'
                              : 'pi pi-check-circle'
                          "
                          aria-hidden="true"
                        ></i>
                        Saved work
                      </dt>
                      <dd>
                        <strong>{{ reviewSavedStateLabel() }}</strong>
                        <span>{{ reviewSavedStateDetail() }}</span>
                      </dd>
                    </div>
                    <div [attr.data-status]="draft.validationStatusCode">
                      <dt>
                        <i
                          [class]="
                            draft.validationStatusCode === 'valid'
                              ? 'pi pi-check-circle'
                              : draft.validationStatusCode === 'invalid'
                                ? 'pi pi-exclamation-circle'
                                : 'pi pi-clock'
                          "
                          aria-hidden="true"
                        ></i>
                        Validation
                      </dt>
                      <dd>
                        <strong>{{ validationStateLabel() }}</strong>
                        <span>{{ reviewValidationDetail() }}</span>
                      </dd>
                    </div>
                    <div
                      [attr.data-status]="reviewExecutionSupport().status"
                      data-testid="activation-support"
                    >
                      <dt>
                        <i
                          [class]="draft.isExecutable ? 'pi pi-shield' : 'pi pi-lock'"
                          aria-hidden="true"
                        ></i>
                        Activation &amp; process runs
                      </dt>
                      <dd>
                        <strong>{{ reviewExecutionSupport().label }}</strong>
                        <span>{{ reviewExecutionSupport().detail }}</span>
                      </dd>
                    </div>
                  </dl>
                </details>
              </section>
              <div class="workflow-review__readiness">
                <section
                  class="workflow-review__issues"
                  aria-labelledby="workflow-draft-validation-title"
                >
                  <header>
                    <div>
                      <h4
                        #reviewFindingsHeading
                        id="workflow-draft-validation-title"
                        tabindex="-1"
                      >
                        Validation findings
                      </h4>
                    </div>
                    <span>
                      {{ reviewFindingCountLabel() }}
                    </span>
                  </header>

                  <p
                    *ngIf="hasOpenDraftUnsavedChanges()"
                    class="workflow-review__warning"
                    role="status"
                    aria-live="polite"
                    data-testid="review-local-changes-excluded"
                  >
                    <strong>Local changes are not included.</strong>
                    These findings describe saved revision {{ draft.revision }}.
                    {{
                      isArchivedDraft()
                        ? "The local copy cannot change this archived source."
                        : "Save the open work to check the updated revision automatically."
                    }}
                  </p>

                  <p
                    *ngIf="!canValidate() && !isArchivedDraft()"
                    class="workflow-review__permission-note"
                  >
                    You can inspect the recorded findings. A workflow validator must
                    run validation after the saved draft changes.
                  </p>

                  <p
                    *ngIf="
                      diagnostics().length === 0 &&
                      draft.validationStatusCode === 'valid'
                    "
                    class="workflow-review__empty"
                    role="status"
                  >
                    <i class="pi pi-check-circle" aria-hidden="true"></i>
                    <span>
                      <strong>No validation issues.</strong>
                    </span>
                  </p>
                  <p
                    *ngIf="
                      diagnostics().length === 0 &&
                      draft.validationStatusCode !== 'valid'
                    "
                    class="workflow-review__empty"
                    role="status"
                  >
                    No recorded findings for this saved revision.
                  </p>

                  <section
                    *ngFor="let group of reviewDiagnosticGroups()"
                    class="workflow-review__finding-group"
                    [attr.aria-labelledby]="'workflow-findings-' + group.id"
                  >
                    <header>
                      <h5 [id]="'workflow-findings-' + group.id">
                        {{ group.title }}
                        <span>({{ group.findings.length }})</span>
                      </h5>
                    </header>
                    <ul class="workflow-review__diagnostics">
                      <li
                        *ngFor="let finding of group.findings"
                        [attr.data-severity]="finding.diagnostic.severityCode"
                        [attr.data-finding-id]="finding.id"
                      >
                        <ng-container *ngIf="finding.diagnostic as diagnostic">
                          <div class="workflow-review__diagnostic-heading">
                            <span>{{
                              diagnostic.severityCode
                                ? (diagnostic.severityCode | titlecase)
                                : "Unspecified severity"
                            }}</span>
                            <strong [class.visually-hidden]="diagnosticAreaLabel(diagnostic) === group.title">{{ diagnosticAreaLabel(diagnostic) }}</strong>
                            <small *ngIf="diagnosticLocationLabel(diagnostic) !== group.title && diagnosticLocationLabel(diagnostic) !== diagnosticAreaLabel(diagnostic)">{{ diagnosticLocationLabel(diagnostic) }}</small>
                          </div>
                          <p>{{ diagnostic.message }}</p>
                          <div class="workflow-review__diagnostic-actions">
                            <button
                              [wrap]="true"
                              uiButton
                              [text]="true"
                              [outlined]="false"
                              *ngIf="canFocusDiagnostic(diagnostic)"
                              type="button"
                              class="workflow-drafts__row-button"
                              [disabled]="isDraftEditorInteractionBlocked()"
                              (click)="
                                focusValidationDiagnostic(diagnostic, finding.id)
                              "
                            >
                              {{ diagnosticFocusLabel(diagnostic) }}
                            </button>
                            <details>
                              <summary>Technical details</summary>
                              <dl>
                                <div>
                                  <dt>Diagnostic code</dt>
                                  <dd>
                                    <code>{{ diagnostic.code }}</code>
                                  </dd>
                                </div>
                                <div>
                                  <dt>Document path</dt>
                                  <dd>
                                    <code>{{ diagnostic.path }}</code>
                                  </dd>
                                </div>
                              </dl>
                            </details>
                          </div>
                        </ng-container>
                      </li>
                    </ul>
                  </section>
                </section>
              </div>
              <details
                *ngIf="selectedMapNode()?.typeCode === 'human_step'"
                class="workflow-step-checklist workflow-step-checklist--review"
                data-testid="step-setup-checklist"
              >
                <summary>
                  Optional step checklist ·
                  {{ selectedMapNode()?.displayName || selectedMapNode()?.stepCode }}
                </summary>
                <p>
                  Local settings for this step. The saved validation result is shown
                  above.
                </p>
                <ul>
                  <li
                    *ngFor="let item of selectedStepChecklist()"
                    [attr.data-testid]="'step-checklist-' + item.id"
                  >
                    <div>
                      <strong>{{ item.title }}</strong
                      ><span
                        class="workflow-step-checklist__status"
                        [class.workflow-step-checklist__status--attention]="
                          item.status === 'attention'
                        "
                        >{{ checklistStatusLabels[item.status] }}</span
                      >
                      <p>{{ item.detail }}</p>
                    </div>
                    <button
                      uiButton
                      type="button"
                      [disabled]="isDraftEditorInteractionBlocked()"
                      (click)="openStepChecklistTarget(item.target)"
                    >
                      {{ item.action }}
                    </button>
                  </li>
                </ul>
              </details>
            </section>

            <section
              id="workflow-review-panel-changes"
              class="workflow-review__panel"
              role="region"
              aria-labelledby="workflow-review-tab-changes"
              [hidden]="activeReviewView() !== 'changes'"
            >
              <phms-platform-workflow-draft-semantic-diff
                [draft]="draft"
                [localChangesPending]="hasOpenDraftUnsavedChanges()"
                [active]="
                  activeWorkbenchView() === 'review' &&
                  activeReviewView() === 'changes'
                "
                [interactionBlocked]="isDraftEditorInteractionBlocked()"
                (changeFocusRequested)="focusSemanticChange($event)"
              ></phms-platform-workflow-draft-semantic-diff>
            </section>
          </ui-tab-group>
        </div>
      </section>

    </ui-tab-group>
  </section>
</section>
